// The solid pieces a bench is built from in the section and the 3D view: its
// slats (one per slat, the gaps open), the frame they are fixed to, the trestles
// that carry it, and — when the bench says so — the skirting boards under its
// front board; a steam bench's profile sweep instead. Pure. The plan
// draws the slats from the same quads (`polyBenchSlatQuads`,
// `polyBenchCornerSlatQuads`), so all three agree; the rest stands under the
// slats and is not drawn on the plan. What a room's contents carve out of its
// benches (`benchHoles`) is cut here, once, for every output (`carvedSeat`).
//
// The construction is Stenal's (`cavalletto.dxf`): a frame of 40×45 timber —
// a rail on the front and back edges and a stud across every ≤ 400 mm — on
// trestles of the same timber where the user placed a support (`benchSupports.ts`;
// none: the bench is fixed to the walls). The LAST board, counting from the wall,
// is the front board: the frame stops at its back face, and the skirting hangs
// behind it.

import type {
  BenchProfilePoint,
  BenchSupport,
  InnerWall,
  ProductInstance,
  ProductTemplate,
  SkirtingSpec,
  Vec2,
} from '../shared/types';
import {
  DEFAULT_SKIRTING,
  skirtingOf,
} from '../shared/benchPresets';
import {
  bandPathAt,
  profileExtent,
  profileFilled,
} from './benchProfile';
import {
  BENCH_TIER_COLORS,
  benchSlatSpans,
  type BenchTier,
  STEAM_BENCH_TIER_COLORS,
} from './benches';
import {
  benchBase,
  benchHeight,
  benchThickness,
  DEFAULT_SLAT_THICKNESS,
  objectHeight,
  slatBase,
} from './heights';
import {
  bandAt,
  benchDrawOrder,
  innerBenchPaintOrder,
  isPolyRoom,
  partitionClips,
  polyBenchBands,
  type PolyBenchCornerBand,
  polyBenchCornerBands,
  polyBenchCornerSlatQuads,
  polyBenchLifts,
  polyBenchSlatQuads,
  polyBenchTuckShifts,
  polyEdges,
  type PolyInnerBenchBand,
  polyInnerBenchBands,
  polyInnerBenchSlatQuads,
  RADIUS_EPS,
  shiftBandWallward,
  wallView,
} from './polyRoom';
import {
  instanceToWorld,
  pointInPolygon,
  ribbonJoint,
} from './geometry';
import {
  polygonBoolean,
  type RingPoly,
} from './polybool';
import { grilleTop } from './grilleFigures';
import {
  cornerSupportsOn,
  flushOnTier,
  partitionSupportsOn,
  roundSupportsOn,
  runStation,
  segSupportsOn,
  type SupportRun,
  supportsAlong,
  TRESTLE_WIDTH,
  trestleCentre,
  wallSupportsOn,
} from './benchSupports';

export {
  TRESTLE_SPACING,
  TRESTLE_WIDTH,
} from './benchSupports';

/** Plan width of a frame rail or stud (mm). */
export const FRAME_RAIL = 40;
/** Height of the frame under the slats (mm). */
export const FRAME_HEIGHT = 45;
/** The most a frame's studs stand apart (mm). */
export const STUD_SPACING = 400;
/** A trestle post's depth and a trestle rail's height (mm). */
export const TRESTLE_TIMBER = 45;
/** How far the front post stands back from the seat's front edge (mm). */
export const FRONT_POST_SETBACK = 56;
/** The underside of a trestle's bottom rail (mm above the floor). */
export const TRESTLE_FOOT = 50;
/** The most clear height between two trestle rails (mm). */
export const TRESTLE_RAIL_GAP = 400;
/** The depth of a skirting batten, behind the boards (mm). */
export const SKIRTING_BATTEN = 28;
/** A skirting batten's width along the run, centred on the trestle it stands
 *  in front of (mm). */
export const SKIRTING_BATTEN_WIDTH = 40;
/** How far a skirting lifts: its battens stop this far under the frame at their
 *  front face, so it can be lifted and slid away to clean under the bench
 *  (cavalletto.dxf) (mm). */
export const SKIRTING_LIFT = 30;
/** A skirting batten's top falls toward the back at this angle (degrees). */
export const SKIRTING_BATTEN_SLANT = 30;
/** A skirting batten's foot (cavalletto.dxf): on the floor it rests on a block
 *  fixed to the post, `block` high at its front face, across a `gap` sloping
 *  `slant`° down toward the back; on a seat it stands `notch` down into the slat
 *  under it (mm, degrees). */
export const SKIRTING_FOOT = { block: 35, slant: 22.5, gap: 2, notch: 10 } as const;
/** A trestle post's levelling foot, simplified from cavalletto.dxf: a pad
 *  `width` square and `pad` high, a cap over it tapering to `top` wide, the post
 *  standing on it at `height` (mm). */
export const TRESTLE_LEVELLER = { width: 39.5, pad: 4, top: 32, height: 12 } as const;
/** A skirting's lowest board, cut to reach the clearance, is left out when the
 *  cut would leave it lower than this (mm). */
export const SKIRTING_MIN_CUT = 20;

/** The LED channel an A1 light sits in under a bench's front, as Stenal builds
 *  it (cavalletto.dxf): an aluminium profile `width` × `height`, its lens down, at
 *  the top of a groove carved `groove.width` wide and `groove.height` high up
 *  into the bottom of the front board (`groove.intoFront` of it) and of the
 *  closure's top board behind it (the rest). */
export const A1_CHANNEL = { width: 22, height: 10.5, groove: { width: 25, height: 20, intoFront: 15 } } as const;

export type BenchPart = 'slat' | 'frame' | 'support' | 'skirting';

/** A solid piece: a plan footprint and its height band; a trestle's pieces carry
 *  the support they stand for. A piece that is no block from `base` to `top`
 *  carries a `sweep`: its profile (depth x from its back face, height y) swept
 *  along `pathAt(x)` — a steam bench's, a skirting batten's slanted top — and
 *  `quad`, a straight piece's `[backStart, backEnd, frontEnd, frontStart]`
 *  (absent on a rounded-corner piece); its band then bounds the profile. */
export interface Solid {
  part: BenchPart;
  poly: Vec2[];
  /** Rings cut clean through it: what stands in its room inside it (`carvedSeat`). */
  holes?: Vec2[][];
  base: number;
  top: number;
  support?: string;
  sweep?: { profile: BenchProfilePoint[]; pathAt: (x: number) => Vec2[]; quad?: Vec2[] };
}

/** One placed skirting board: its height band and thickness. */
export interface SkirtingPiece { z0: number; z1: number; thickness: number }

/**
 * Where a skirting's boards go, top first: from `top` (under the front board,
 * or the frame) down to `base` + the clearance (the floor, or the seat below).
 * The board list repeats — each board after its own gap — while a whole board
 * still fits; the next one is then CUT to end on the clearance, and left out
 * when that would leave it under {@link SKIRTING_MIN_CUT}.
 */
export function skirtingLayout(spec: SkirtingSpec, top: number, base: number): SkirtingPiece[] {
  const out: SkirtingPiece[] = [];
  const n = spec.boards.length;
  if (!n || !spec.boards.some((b) => b > 0)) return out;
  const floor = base + Math.max(0, spec.clearance);
  let z = top;
  for (let i = 0; i < 10000; i++) {
    const k = i % n;
    const h = spec.boards[k];
    const t = spec.thickness[k] > 0 ? spec.thickness[k] : 0;
    z -= Math.max(0, spec.gaps[k] ?? 0);
    if (!(h > 0) || !(t > 0)) continue;
    if (z - h >= floor - 1e-9) { out.push({ z0: z - h, z1: z, thickness: t }); z -= h; continue; }
    if (z - floor >= SKIRTING_MIN_CUT) out.push({ z0: floor, z1: z, thickness: t });
    break;
  }
  return out;
}

/** A bench run, in the slat frame: `d` mm from the wall-side face toward the
 *  front. `depth` is the FRAME's — up to the front board's back face. */
export interface BenchRun {
  /** The band between depths d0 and d1 over the whole run. */
  strip: (d0: number, d1: number) => Vec2[];
  /** Where the skirting is laid, where its ends are not the frame's (a
   *  standalone bench's corner: it meets the other segment's skirting). */
  skirtingStrip?: (d0: number, d1: number) => Vec2[];
  /** A straight run: the point at fraction `u` along it, depth `d`. A rounded
   *  corner piece has none, so it carries no studs (its trestles are laid by
   *  `trestleAt`). */
  at?: (u: number, d: number) => Vec2;
  /** The point `s` mm along the run and `d` deep, square to it: where a trestle
   *  is laid (a mitred end does not slant it). */
  across?: (s: number, d: number) => Vec2;
  /** A trestle centred at station `at`, laid straight: the point `x` mm across
   *  it and `d` deep. Absent: `across(at + x, d)`, the run being straight; a
   *  rounded corner piece lays each one radially, square to its arc. */
  trestleAt?: (at: number) => (x: number, d: number) => Vec2;
  /** A point's station along the run (mm). Absent: projected square to `across`. */
  stationOf?: (p: Vec2) => number;
  /** The trestles: each one's centre in `across`'s station and the support it
   *  stands for. None: the bench is fixed to the walls. */
  trestles?: Array<{ id: string; at: number }>;
  /** The bench's two ends in `across`'s station — what a trestle's distances are
   *  measured to where no other trestle stands between. */
  ends?: [number, number];
  /** A straight run's slatted mitres where its frame runs through: the end (0
   *  its start, 1 its end) and the mitre line's point `d` mm deep. The stud at
   *  that end lies on the line, under the slats' joint. */
  mitres?: Array<{ end: 0 | 1; at: (d: number) => Vec2 }>;
  /** Where its supports are stored (a straight run only): the host, the length a
   *  partition's / segment's fraction is of, the stored span (a wall bench's
   *  `start` / `end`), its tier on that host and its plan footprint as painted —
   *  `supportRuns` builds a `SupportRun` from it. */
  support?: { kind: SupportRun['kind']; host: SupportRun['host']; len: number; start: number; end: number; tier: number; face: Vec2[] };
  /** Run length (mm), along its middle. */
  len: number;
  depth: number;
  /** The seat's top (mm). */
  top: number;
  /** The seat slats' thickness: the frame stands under it. */
  slab: number;
  /** The front board's underside (mm), when it hangs below the frame: the
   *  skirting starts under it. */
  frontBase?: number;
  /** How far the tier in front reaches behind the frame's front: its front post
   *  stands back at least that far. */
  tuck?: number;
  /** The top of the tier in front's trestle top rail (its frame's underside): a
   *  mid rail is laid level with it, so the two trestles' rails line up. */
  frontRail?: number;
  /** The skirting's boards; none = an open front. */
  skirting?: SkirtingSpec | null;
  /** What the skirting stands on (the floor, or the seat below). */
  skirtingBase?: number;
  /** On the floor: the top of what covers it (a floor grille, `grilleTop`), the
   *  skirting standing on that. */
  skirtingFloor?: number;
  /** How far the skirting stands behind the front board. */
  skirtingSetBack?: number;
  /** An A1 light's channel runs along it: the front board and the closure's top
   *  board are carved for it (`A1_CHANNEL`). */
  groove?: boolean;
}

/** What a run's skirting stands on: the seat below, else the floor's cover. */
const skirtingStand = (r: BenchRun): number => ((r.skirtingBase ?? 0) > 1e-6 ? r.skirtingBase! : r.skirtingFloor ?? 0);

/** `n` + 1 centres from `a` to `b`, evenly (one, in the middle, for n = 0). */
const spread = (a: number, b: number, n: number): number[] =>
  n <= 0 ? [(a + b) / 2] : Array.from({ length: n + 1 }, (_, k) => a + ((b - a) * k) / n);

/** How deep a run's trestle reaches (mm from its wall-side face): to its front
 *  post's front face, set back from the frame's front past the tier tucked under
 *  it and the skirting's board and batten. */
function trestleReach(r: BenchRun): number {
  // Only boards that are placed push it back: a skirting no board fits under
  // (a bench too low) leaves the post at its own setback.
  const placed = r.skirting ? skirtingLayout(r.skirting, Math.min(r.top - r.slab - FRAME_HEIGHT, r.frontBase ?? Infinity), skirtingStand(r)) : [];
  const boardT = r.skirting ? Math.max(0, ...r.skirting.thickness) : 0;
  const setback = Math.max(FRONT_POST_SETBACK, r.tuck ?? 0, placed.length && boardT > 0 ? boardT + SKIRTING_BATTEN + runSkirtingBack(r) : 0);
  return r.depth - setback;
}

/** How far a skirting stands behind its frame's front (mm): far enough that
 *  its batten meets the trestle's front post — set back FRONT_POST_SETBACK, or
 *  past the tier tucked under it —, or further by the bench's own
 *  `skirtingSetBack`. `boardT` is its thickest board. */
export function skirtingBack(boardT: number, tuck = 0, setBack = 0): number {
  return Math.max(0, setBack, Math.max(FRONT_POST_SETBACK, tuck) - boardT - SKIRTING_BATTEN);
}

/** `skirtingBack` of a skirting's boards on a frame `depth` deep, kept inside it. */
const runSkirtBack = (spec: SkirtingSpec, tuck: number, setBack: number | undefined, depth: number): number => {
  const t = Math.max(0, ...spec.thickness);
  return Math.max(0, Math.min(skirtingBack(t, tuck, setBack ?? 0), depth - t - SKIRTING_BATTEN));
};

/** `skirtingBack` on a run. */
const runSkirtingBack = (r: BenchRun): number => r.skirting ? runSkirtBack(r.skirting, r.tuck ?? 0, r.skirtingSetBack, r.depth) : 0;

/** Whether a trestle fits under the run's frame at all. */
const trestleFits = (r: BenchRun): boolean => r.top - r.slab - FRAME_HEIGHT > TRESTLE_TIMBER;

/** A run's trestle centred at `at`, laid straight: `x` mm across it, `d` deep. */
const trestleFrame = (r: BenchRun, across: NonNullable<BenchRun['across']>, at: number): ((x: number, d: number) => Vec2) =>
  r.trestleAt ? r.trestleAt(at) : (x, d) => across(at + x, d);

/** The plan footprint of a trestle centred `at` on a run — its top rail, from
 *  the back post to the front one — or null where none can stand. */
export function trestleFootprint(r: BenchRun, at: number): Vec2[] | null {
  if (!r.across || !trestleFits(r)) return null;
  const h = TRESTLE_WIDTH / 2, d1 = Math.max(TRESTLE_TIMBER, trestleReach(r)), lay = trestleFrame(r, r.across, at);
  return [lay(-h, 0), lay(h, 0), lay(h, d1), lay(-h, d1)];
}

/** Keep the part of convex polygon `poly` inside convex polygon `clip`
 *  (Sutherland–Hodgman, either winding). */
function clipConvex(poly: Vec2[], clip: Vec2[]): Vec2[] {
  let area = 0;
  for (let i = 0; i < clip.length; i++) { const a = clip[i], b = clip[(i + 1) % clip.length]; area += a.x * b.y - b.x * a.y; }
  const sgn = area >= 0 ? 1 : -1;
  let out = poly;
  for (let i = 0; i < clip.length && out.length; i++) {
    const a = clip[i], b = clip[(i + 1) % clip.length];
    const side = (p: Vec2): number => sgn * ((b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x));
    const next: Vec2[] = [];
    for (let k = 0; k < out.length; k++) {
      const p = out[k], q = out[(k + 1) % out.length], fp = side(p), fq = side(q);
      if (fp >= 0) next.push(p);
      if ((fp > 0 && fq < 0) || (fp < 0 && fq > 0)) next.push(lerp(p, q, fp / (fp - fq)));
    }
    out = next;
  }
  return out;
}

/** Convex `poly` split by convex `clip`: the part inside it, and the rest as
 *  convex pieces round it (each cut off along one of `clip`'s edges). */
function splitConvex(poly: Vec2[], clip: Vec2[]): { inside: Vec2[]; outside: Vec2[][] } {
  let area = 0;
  for (let i = 0; i < clip.length; i++) { const a = clip[i], b = clip[(i + 1) % clip.length]; area += a.x * b.y - b.x * a.y; }
  const sgn = area >= 0 ? 1 : -1;
  const outside: Vec2[][] = [];
  let rest = poly;
  for (let i = 0; i < clip.length && rest.length >= 3; i++) {
    const a = clip[i], b = clip[(i + 1) % clip.length];
    const side = (p: Vec2): number => sgn * ((b.x - a.x) * (p.y - a.y) - (b.y - a.y) * (p.x - a.x));
    const keep = (k: number): Vec2[] => {
      const out: Vec2[] = [];
      for (let j = 0; j < rest.length; j++) {
        const p = rest[j], q = rest[(j + 1) % rest.length], fp = k * side(p), fq = k * side(q);
        if (fp >= 0) out.push(p);
        if ((fp > 0 && fq < 0) || (fp < 0 && fq > 0)) out.push(lerp(p, q, fp / (fp - fq)));
      }
      return out;
    };
    const out = keep(-1);
    if (out.length >= 3 && polyArea(out) > 0.01) outside.push(out);
    rest = keep(1);
  }
  return { inside: rest.length >= 3 && polyArea(rest) > 0.01 ? rest : [], outside };
}

const polyArea = (q: Vec2[]): number => Math.abs(q.reduce((a, p, i) => { const n = q[(i + 1) % q.length]; return a + p.x * n.y - n.x * p.y; }, 0)) / 2;

/**
 * The studs across a straight run's frame, between its two rails: one at each
 * end following that end's cut — and, at a slatted mitre where its frame runs
 * through, one more from it along the mitre line under the slats' joint — and
 * between them as many as keep them within `STUD_SPACING`, every one of those
 * square to the run whatever its ends do. Each is cut to the frame.
 */
function frameStuds(r: BenchRun, at: NonNullable<BenchRun['at']>): Vec2[][] {
  const sub = (p: Vec2, q: Vec2): Vec2 => ({ x: p.x - q.x, y: p.y - q.y });
  const D = r.depth, R = FRAME_RAIL, L = r.len, h = R / 2;
  if (!(L > R)) return [];
  const m = D / 2, a = at(0, m), b = at(1, m);
  const ml = dist(a, b) || 1, u = { x: (b.x - a.x) / ml, y: (b.y - a.y) / ml };
  const toward = sub(at(0.5, D), at(0.5, 0));
  const nrm = u.x * toward.y - u.y * toward.x >= 0 ? { x: -u.y, y: u.x } : { x: u.y, y: -u.x };
  const frame = r.strip(0, D);
  const station = (p: Vec2): number => ((p.x - a.x) * u.x + (p.y - a.y) * u.y) * (L / ml);
  // Where the line through `p` along `dir` crosses depth line `d`.
  const onDepth = (p: Vec2, dir: Vec2, d: number): Vec2 => {
    const q = at(0, d), w = sub(at(1, d), q);
    const den = dir.x * w.y - dir.y * w.x;
    if (Math.abs(den) < 1e-12) return p;
    const t = ((q.x - p.x) * w.y - (q.y - p.y) * w.x) / den;
    return { x: p.x + dir.x * t, y: p.y + dir.y * t };
  };
  // A stud `R` wide along the run whose middle runs along `dir` through `p`.
  const along = (p: Vec2, dir: Vec2): Vec2[] => {
    const s = Math.abs(dir.x * u.y - dir.y * u.x) || 1, w = h / s;
    const p0 = onDepth(p, dir, R), p1 = onDepth(p, dir, D - R);
    const sh = (q: Vec2, k: number): Vec2 => ({ x: q.x + u.x * w * k, y: q.y + u.y * w * k });
    return clipConvex([sh(p0, -1), sh(p0, 1), sh(p1, 1), sh(p1, -1)], frame);
  };
  const mitreOf = (end: 0 | 1) => r.mitres?.find((x) => x.end === end);
  const m0 = mitreOf(0), m1 = mitreOf(1);
  const diag = (x: { at: (d: number) => Vec2 }): Vec2[] => {
    const p = x.at(m), q = x.at(D - R), dir = sub(q, x.at(R)), l = Math.hypot(dir.x, dir.y) || 1;
    return along(p, { x: dir.x / l, y: dir.y / l });
  };
  const lo = m0 ? station(m0.at(m)) : h, hi = m1 ? station(m1.at(m)) : L - h;
  const cs = spread(lo, Math.max(lo, hi), Math.max(1, Math.ceil((hi - lo) / STUD_SPACING)));
  // An end stud lies along its end's cut, `R` along the run from it; a mitred
  // end keeps it too, the diagonal running from it.
  const endStud = (end: 0 | 1): Vec2[] => {
    const p0 = at(end, R), p1 = at(end, D - R), k = end === 0 ? R : -R;
    return [p0, { x: p0.x + u.x * k, y: p0.y + u.y * k }, { x: p1.x + u.x * k, y: p1.y + u.y * k }, p1];
  };
  const diagFrom = (x: { at: (d: number) => Vec2 }, end: 0 | 1): Vec2[][] => splitConvex(diag(x), endStud(end)).outside;
  const out = cs.map((c, k) => {
    if (k === 0) return endStud(0);
    if (k === cs.length - 1) return endStud(1);
    return along(lerp(a, b, c / L), nrm);
  });
  if (m0) out.push(...diagFrom(m0, 0));
  if (m1) out.push(...diagFrom(m1, 1));
  return out.filter((q) => q.length >= 3);
}

/**
 * One Stenal trestle, as `support` solids tagged `id`: laid by `lay(x, d)` — `x`
 * across it, `d` from its back —, its back post flush with d = 0, its front
 * post's front face at `front`, its top rail's top at `top`. A back post and a
 * front post on levelling feet, a top rail under what it holds, a bottom rail
 * and as many rails between as keep every clear gap within TRESTLE_RAIL_GAP —
 * one at `level` when given, as cavalletto.dxf joins two tiers' trestles. A
 * trestle too shallow for two posts is its back post and top rail alone.
 */
export function trestlePieces(lay: (x: number, d: number) => Vec2, front: number, top: number, id: string, level?: number): Solid[] {
  const out: Solid[] = [];
  const P = TRESTLE_TIMBER;
  const push = (poly: Vec2[], base: number, z1: number, sweep?: Solid['sweep']): void => {
    const b = Math.max(0, base);
    if (z1 - b > 1e-6 && poly.length >= 3) out.push({ part: 'support', poly, base: b, top: z1, support: id, ...(sweep ? { sweep } : {}) });
  };
  const h = TRESTLE_WIDTH / 2;
  const postTop = top - P;
  const rails: Array<[number, number]> = [];
  const spaced = (lo: number, hi: number): void => {
    const mids = Math.max(0, Math.ceil((hi - lo) / TRESTLE_RAIL_GAP) - 1);
    const gap = (hi - lo - mids * P) / (mids + 1);
    for (let k = 1; k <= mids; k++) {
      const z = lo + k * gap + (k - 1) * P;
      rails.push([z, z + P]);
    }
  };
  const lo = TRESTLE_FOOT + P;
  if (lo <= postTop) {
    rails.push([TRESTLE_FOOT, lo]);
    if (postTop - lo > TRESTLE_RAIL_GAP && level != null && level - P >= lo && level <= postTop) {
      spaced(lo, level - P);
      rails.push([level - P, level]);
      spaced(level, postTop);
    } else spaced(lo, postTop);
  }
  const twoPosts = front - P > P;
  const F = TRESTLE_LEVELLER, fw = F.width / 2, taper = (F.width - F.top) / 2;
  const sq = (d0: number, d1: number): Vec2[] => [lay(-h, d0), lay(h, d0), lay(h, d1), lay(-h, d1)];
  // Each post on a levelling foot: its pad, then its cap tapering up to it.
  const foot = (dc: number): void => {
    const q = [lay(-fw, dc - fw), lay(fw, dc - fw), lay(fw, dc + fw), lay(-fw, dc + fw)];
    push(q, 0, F.pad);
    const cap = [{ x: 0, y: F.pad }, { x: F.width, y: F.pad }, { x: F.width - taper, y: F.height }, { x: taper, y: F.height }];
    push(q, F.pad, F.height, { profile: cap, pathAt: bandPathAt(q, F.width), quad: q });
  };
  push(sq(0, P), F.height, postTop);
  foot(P / 2);
  push(sq(0, Math.max(P, front)), postTop, top);
  if (!twoPosts) return out;
  push(sq(front - P, front), F.height, postTop);
  foot(front - P / 2);
  for (const [z0, z1] of rails) push(sq(P, front - P), z0, z1);
  return out;
}

/**
 * The frame, trestles and skirting of one bench run (not its slats, the front
 * board included). A piece with no height above the floor is left out.
 */
export function benchStructure(r: BenchRun): Solid[] {
  const out: Solid[] = [];
  const D = r.depth, R = FRAME_RAIL;
  const push = (part: BenchPart, poly: Vec2[], base: number, top: number, support?: string, sweep?: Solid['sweep']): void => {
    const b = Math.max(0, base);
    if (top - b > 1e-6 && poly.length >= 3) out.push({ part, poly, base: b, top, ...(support != null ? { support } : {}), ...(sweep ? { sweep } : {}) });
  };
  const frameTop = r.top - r.slab, frameBase = frameTop - FRAME_HEIGHT;
  const spec = r.skirting ?? null;
  const boardT = spec ? Math.max(0, ...spec.thickness) : 0;
  if (D > 2 * R) {
    push('frame', r.strip(0, R), frameBase, frameTop);
    push('frame', r.strip(D - R, D), frameBase, frameTop);
  } else push('frame', r.strip(0, D), frameBase, frameTop);
  if (D > 2 * R && r.at) for (const q of frameStuds(r, r.at)) push('frame', q, frameBase, frameTop);

  // Trestles, where supports are placed: a back post flush with the wall-side
  // face, a front post set back from the front edge, a top rail under the frame,
  // a bottom rail and as many rails between as keep every clear gap within
  // TRESTLE_RAIL_GAP — the first level with the tier in front's top rail, as
  // cavalletto.dxf joins the two trestles.
  const front = trestleReach(r);
  const across = r.across;
  const trestles = across && trestleFits(r) ? r.trestles ?? [] : [];
  if (across) for (const t of trestles) out.push(...trestlePieces(trestleFrame(r, across, t.at), front, frameBase, t.id, r.frontRail));

  // Skirting: boards from under the front board (or the frame) down to the
  // clearance (`skirtingLayout`), each against the front — set back behind it
  // as far as its batten meets the front post (`skirtingBack`) —; a batten in front of each trestle's front post holds
  // them (none where no support is placed), stopping SKIRTING_LIFT under the
  // frame, its top slanted toward the back.
  const pieces = spec ? skirtingLayout(spec, Math.min(frameBase, r.frontBase ?? Infinity), skirtingStand(r)) : [];
  const sb = runSkirtingBack(r);
  const skirtStrip = r.skirtingStrip ?? r.strip;
  // Its top board, behind the front board from the board's lower edge up to the
  // frame — carved where an A1 light's channel runs.
  if (spec && r.frontBase != null && frameBase - r.frontBase > 0.5) {
    const t = spec.thickness[0] > 0 ? spec.thickness[0] : boardT, b0 = D - t - sb, b1 = D - sb, z0 = r.frontBase;
    const G = A1_CHANNEL.groove, zc = Math.min(frameBase, z0 + G.height);
    if (r.groove) {
      const g0 = D - (G.width - G.intoFront), g1 = D + G.intoFront;
      for (const [a, b] of [[b0, Math.min(b1, g0)], [Math.max(b0, g1), b1]]) if (b - a > 0.5) push('skirting', skirtStrip(a, b), z0, zc);
      push('skirting', skirtStrip(b0, b1), zc, frameBase);
    } else push('skirting', skirtStrip(b0, b1), z0, frameBase);
  }
  for (const b of pieces) push('skirting', skirtStrip(D - b.thickness - sb, D - sb), b.z0, b.z1);
  if (pieces.length) {
    const lowest = pieces[pieces.length - 1].z0, B = SKIRTING_BATTEN, F = SKIRTING_FOOT;
    const zf = frameBase - SKIRTING_LIFT, zb = Math.max(lowest, zf - B * Math.tan((SKIRTING_BATTEN_SLANT * Math.PI) / 180));
    // Its foot: on a seat, down into the slat under it (`notchSeats`); on the
    // floor, over its block across the sloping joint — where the block leaves
    // the batten room.
    const seat = (r.skirtingBase ?? 0) > 1e-6, rise = B * Math.tan((F.slant * Math.PI) / 180);
    const block = !seat && zb - (lowest + F.block + F.gap) > SKIRTING_MIN_CUT;
    const foot1 = seat ? r.skirtingBase! - F.notch : block ? lowest + F.block + F.gap : lowest;
    const foot0 = block ? foot1 - rise : foot1;
    const profile = [{ x: 0, y: foot0 }, { x: B, y: foot1 }, { x: B, y: zf }, { x: 0, y: zb }];
    const blockProfile = [{ x: 0, y: lowest }, { x: B, y: lowest }, { x: B, y: lowest + F.block }, { x: 0, y: lowest + F.block - rise }];
    const w = SKIRTING_BATTEN_WIDTH / 2, d0 = D - boardT - B - sb, d1 = D - boardT - sb;
    if (across && zf - foot1 > 1e-6) for (const t of trestles) {
      const lay = trestleFrame(r, across, t.at);
      const q = [lay(-w, d0), lay(w, d0), lay(w, d1), lay(-w, d1)];
      push('frame', q, foot0, zf, undefined, { profile, pathAt: bandPathAt(q, B), quad: q });
      if (block) push('frame', q, lowest, lowest + F.block, undefined, { profile: blockProfile, pathAt: bandPathAt(q, B), quad: q });
    }
  }
  return out;
}

type SlatSpec = { slats?: number[]; gaps?: number[]; slatThickness?: number[]; thickness?: number };

/** The front board of a slatted bench — its LAST board, counting from the wall:
 *  `d` its back face (where the frame stops), `width` its plan width, `hang` how
 *  far it hangs from the seat top. None with fewer than two boards. */
export function frontBoard(b: SlatSpec): { d: number; width: number; hang: number } | null {
  const spans = benchSlatSpans(b);
  if (spans.length < 2) return null;
  const last = spans[spans.length - 1];
  const own = b.slatThickness?.[spans.length - 1];
  return { d: last.off, width: last.width, hang: typeof own === 'number' && own > 0 ? own : seatSlab(b) };
}

/** Whether a bench's front board hangs below its seat: an A1 light on it sits in
 *  the channel carved into it (`A1_CHANNEL`). */
export function frontTakesChannel(b: SlatSpec): boolean {
  const f = frontBoard(b);
  return !!f && f.hang > seatSlab(b) + 1;
}

/** How far below its seat a front board must hang for the channel's groove to
 *  stay under the frame (mm). */
export function channelHangNeeded(b: SlatSpec): number {
  return seatSlab(b) + FRAME_HEIGHT + A1_CHANNEL.groove.height;
}

/** The seat slats' thickness — every board's but the front board's: what the
 *  frame stands under. */
export function seatSlab(b: SlatSpec): number {
  const n = b.slats?.length ?? 0;
  const seat = n >= 2 ? benchThickness({ slatThickness: b.slatThickness?.slice(0, n - 1), thickness: b.thickness }) : undefined;
  return seat ?? benchThickness(b) ?? DEFAULT_SLAT_THICKNESS;
}

/** One solid piece of a room bench: a local-frame footprint and its height band. */
export interface BenchSolid extends Solid {
  /** The bench band's id, or the rounded-corner piece's. */
  id: string;
  kind: 'band' | 'corner';
  level: BenchTier;
  /** The bench's own colour, when it has one. */
  color?: string;
  /** A bench on a partition face (`InnerWall.benches`), not on a wall. */
  partition?: boolean;
}

type BenchTag = Pick<BenchSolid, 'id' | 'kind' | 'level' | 'color' | 'partition'>;

/** A room bench's run as its lights read it (lights.ts): the plan line at each
 *  depth from its wall-side face, its depth and seat and — a slatted bench's —
 *  its front board and skirting; a steam bench's profile. */
export interface BenchLine {
  /** The bench band's id (a wall's or a partition's), or a rounded corner piece's. */
  id: string;
  kind: 'band' | 'corner';
  partition: boolean;
  /** A corner piece's: the bench it continues. */
  sourceId?: string;
  level: BenchTier;
  /** The plan line `d` mm from the wall-side face (room local): its two ends on
   *  a straight run, the arc round a corner piece. */
  path: (d: number) => Vec2[];
  /** To its front face (mm). */
  depth: number;
  /** The seat's top (mm); a stepped steam bench's lifted. */
  top: number;
  /** A slatted bench's front board: its back face's depth and how far it hangs;
   *  `channel` when it hangs below the seat, so an A1 light sits in the channel
   *  carved into it. */
  front?: { d: number; hang: number; channel?: boolean };
  /** The skirting's front face (its depth, the set-back in) and what it stands on. */
  skirting?: { d: number; base: number };
  /** A plain bench's slab thickness (none: solid to the floor). */
  slab?: number;
  /** A steam bench's profile, lifted by `lift`. */
  profile?: BenchProfilePoint[];
  lift?: number;
}

/** What a room's benches are built from: each piece of a slat, sweep or block,
 *  and each slatted run, whose structure stands under it; and each bench's line. */
interface BenchSink {
  piece: (s: BenchSolid) => void;
  run: (tag: BenchTag, run: BenchRun) => void;
  line?: (l: BenchLine) => void;
  /** Each end of a slatted run that can close (`BenchEndMark`). */
  end?: (e: BenchEndMark) => void;
}

const lerp = (a: Vec2, b: Vec2, t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
const pathLen = (pts: Vec2[]): number => pts.reduce((s, p, k) => (k ? s + Math.hypot(p.x - pts[k - 1].x, p.y - pts[k - 1].y) : 0), 0);
const dist = (a: Vec2, b: Vec2): number => Math.hypot(b.x - a.x, b.y - a.y);
/** The point `m` mm from `a` toward `b`. */
const toward = (a: Vec2, b: Vec2, m: number): Vec2 => lerp(a, b, m / (dist(a, b) || 1));

export type BenchEndSide = 'start' | 'end';

/** One end of a slatted bench run the Bench end tool can close — any end but
 *  one another bench carries on from: out in the room, or against a wall, a
 *  partition or a heater wall, where its boards may still be seen through a
 *  glass or an opening. The bench it ends (a wall band's or a partition tier's
 *  id; a standalone bench's instance id), which end, whether it is closed, each
 *  board's footprint inside the end (its slat's width across, its own thickness
 *  square to the end; back to front: end back, inside back, inside front, end
 *  front), the closure's outline and its inside face — the line the plan draws,
 *  stepping where two boards differ. A room's local frame; world for a
 *  standalone bench. */
export interface BenchEndMark {
  bench: string;
  side: BenchEndSide;
  closed: boolean;
  boards: Vec2[][];
  poly: Vec2[];
  line: Vec2[];
}

/** A run's closed ends: per end whether it is closed, the thickness its frame
 *  and skirting stop at inside them (the seat slats', `seatSlab`), and `floor`,
 *  where a board turned down at a plan point stops. Each board turns down its
 *  own thickness. */
interface Closure { frame: number; start: boolean; end: boolean; floor: (p: Vec2) => number }

/** A run's ends as stored on the bench. */
const closedFlags = (b: { closeStart?: boolean; closeEnd?: boolean } | undefined): [boolean, boolean] => [!!b?.closeStart, !!b?.closeEnd];

/** Where a closure's boards stop over what stands under `p`: the floor or the
 *  seat below (`under`), plus the skirting's clearance. */
const closureClearance = (b: { skirtingBoards?: SkirtingSpec }): number => Math.max(0, b.skirtingBoards?.clearance ?? DEFAULT_SKIRTING.clearance);

/** The steepest an end may slant for a board's thickness to be kept square to
 *  it (the sine of the angle between the run and its end). */
const MIN_END_SIN = 0.2;

/** How far along an edge from its end `a`, toward `b`, a board `t` thick
 *  standing inside that end reaches — `c` the end's other corner: `t` on a
 *  square end, more on one slanted along a wall at an angle, so the board keeps
 *  its thickness square to the end. */
function insetAlong(a: Vec2, b: Vec2, c: Vec2, t: number): number {
  const u = { x: b.x - a.x, y: b.y - a.y }, v = { x: c.x - a.x, y: c.y - a.y };
  const sin = Math.abs(u.x * v.y - u.y * v.x) / ((Math.hypot(u.x, u.y) * Math.hypot(v.x, v.y)) || 1);
  return t / Math.max(sin, MIN_END_SIN);
}

/** A slat quad's (`[backStart, backEnd, frontEnd, frontStart]`) inset along its
 *  edges for a board `t` thick inside its end at `side`. */
const slatInset = (q: Vec2[], side: BenchEndSide, t: number): number =>
  side === 'start' ? insetAlong(q[0], q[1], q[3], t) : insetAlong(q[1], q[0], q[2], t);

/** The same for a run, by its plan points `at(u, d)` (`D` its depth): how far
 *  its frame and skirting move in for a closure `t` thick at that end. */
const runInset = (at: (u: number, d: number) => Vec2, D: number, side: BenchEndSide, t: number): number =>
  side === 'start' ? insetAlong(at(0, 0), at(1, 0), at(0, D), t) : insetAlong(at(1, 0), at(0, 0), at(1, D), t);

/** The point at mid-width of a slat quad's end (`[backStart, backEnd, frontEnd,
 *  frontStart]`), `inset` mm in from it. */
function slatEndPoint(q: Vec2[], side: BenchEndSide, inset: number): Vec2 {
  const back = side === 'start' ? toward(q[0], q[1], inset) : toward(q[1], q[0], inset);
  const front = side === 'start' ? toward(q[3], q[2], inset) : toward(q[2], q[3], inset);
  return lerp(back, front, 0.5);
}

/**
 * A seat slat turned down at its closed ends: one board, L-shaped (U with both
 * ends closed), its turned-down part `T` thick — the slat's own thickness —
 * inside the end, square to it however the end slants, so the bench keeps its
 * length; running down to `bottoms` (per end; null where it is open, and no leg
 * where it would not reach below the slat). Swept across the slat from its
 * front to its back, the profile in the run's length and height. Null when it
 * has no leg: the slat stays a block.
 */
export function closedSlat(quad: Vec2[], base: number, top: number, T: number, bottoms: [number | null, number | null]): Solid | null {
  if (quad.length !== 4) return null;
  const W = (dist(quad[0], quad[1]) + dist(quad[3], quad[2])) / 2;
  const leg = (z: number | null): number | null => (z != null && base - z > 0.5 ? Math.max(0, z) : null);
  const [z0, z1] = [leg(bottoms[0]), leg(bottoms[1])];
  if ((z0 == null && z1 == null) || !(T > 0)) return null;
  const A0 = slatInset(quad, 'start', T), A1 = slatInset(quad, 'end', T);
  if (A0 + A1 >= W) return null;
  const profile: BenchProfilePoint[] = [
    ...(z0 != null ? [{ x: 0, y: z0 }, { x: A0, y: z0 }, { x: A0, y: base }] : [{ x: 0, y: base }]),
    ...(z1 != null ? [{ x: W - A1, y: base }, { x: W - A1, y: z1 }, { x: W, y: z1 }] : [{ x: W, y: base }]),
    { x: W, y: top },
    { x: 0, y: top },
  ];
  // The profile's x runs along the slat from its start, each end's leg exact on
  // both of its edges (a slat mitred at its other end is longer on one); the
  // path runs across it, front to back. No `quad`: a section reads its cut off
  // the swept faces, true to a mitred end.
  const [b0, b1, f1, f0] = quad;
  const along = (a: Vec2, b: Vec2, x: number): Vec2 => {
    const L = dist(a, b);
    return toward(a, b, x <= A0 ? x : x >= W - A1 ? L - (W - x) : A0 + ((x - A0) * (L - A0 - A1)) / (W - A0 - A1));
  };
  return { part: 'slat', poly: quad, base: Math.min(z0 ?? base, z1 ?? base), top, sweep: { profile, pathAt: (x) => [along(f0, f1, x), along(b0, b1, x)] } };
}

/** A slat's turned-down parts as blocks under it (`T` thick inside each closed
 *  end, square to it, from `bottoms` up to `base`): the front board carved for
 *  an A1 channel keeps its carved blocks and stands on these. */
function closureLegs(quad: Vec2[], base: number, T: number, bottoms: [number | null, number | null]): Solid[] {
  const out: Solid[] = [];
  const [b0, b1, f1, f0] = quad;
  const A0 = slatInset(quad, 'start', T), A1 = slatInset(quad, 'end', T);
  if (bottoms[0] != null && base - bottoms[0] > 0.5) out.push({ part: 'slat', poly: [b0, toward(b0, b1, A0), toward(f0, f1, A0), f0], base: Math.max(0, bottoms[0]), top: base });
  if (bottoms[1] != null && base - bottoms[1] > 0.5) out.push({ part: 'slat', poly: [toward(b1, b0, A1), b1, f1, toward(f1, f0, A1)], base: Math.max(0, bottoms[1]), top: base });
  return out;
}

/** A run's plan point at fraction `u` along it and depth `d`, cut back `t0` mm
 *  at its start and `t1` at its end at every depth (along the run: `runInset`):
 *  the frame and skirting stop at a closure's inside face. */
function trimmedAt(at: (u: number, d: number) => Vec2, t0: number, t1: number): (u: number, d: number) => Vec2 {
  if (!(t0 > 0) && !(t1 > 0)) return at;
  return (u, d) => {
    const a = at(0, d), b = at(1, d), l = dist(a, b) || 1;
    const f0 = t0 / l, f1 = 1 - t1 / l;
    return lerp(a, b, f0 + u * (f1 - f0));
  };
}

/** How far a run's frame moves in at its closed ends (along it, mm), and its
 *  plan points so cut back. */
function closedRun(at: (u: number, d: number) => Vec2, D: number, close: Closure | null): { at: (u: number, d: number) => Vec2; t0: number; t1: number } {
  const t0 = close?.start ? runInset(at, D, 'start', close.frame) : 0, t1 = close?.end ? runInset(at, D, 'end', close.frame) : 0;
  return { at: trimmedAt(at, t0, t1), t0, t1 };
}

/** A run's ends as the Bench end tool reads them, from its slat quads (first to
 *  last, wall side out) and each slat's thickness: each end's boards, outline and
 *  inside face, the end closable by `closable` at points just beyond it. */
function runEnds(bench: string, quads: Vec2[][], thick: number[], flags: [boolean, boolean], closable: (p: Vec2) => boolean, out: (e: BenchEndMark) => void): [boolean, boolean] {
  const closed: [boolean, boolean] = [false, false];
  (['start', 'end'] as const).forEach((side, k) => {
    const s = side === 'start';
    // Each quad's end corners and the corners its edges run to: back, front.
    const ends = quads.map((q) => (s ? { b: q[0], bo: q[1], f: q[3], fo: q[2] } : { b: q[1], bo: q[0], f: q[2], fo: q[3] }));
    const A = quads.map((q, i) => slatInset(q, side, thick[i] > 0 ? thick[i] : DEFAULT_SLAT_THICKNESS));
    if (ends.some((e, i) => 2 * A[i] >= Math.min(dist(e.b, e.bo), dist(e.f, e.fo)))) return;
    const { b, bo } = ends[0], { f, fo } = ends[ends.length - 1];
    // Just beyond the end, across its depth.
    const probe = [0.2, 0.5, 0.8].map((t) => { const p = lerp(b, f, t), q = lerp(bo, fo, t); return toward(p, q, -5); });
    if (!probe.every(closable)) return;
    const boards = ends.map((e, i) => [e.b, toward(e.b, e.bo, A[i]), toward(e.f, e.fo, A[i]), e.f]);
    // The inside face, back to front: across each board, then a step across
    // the gap to the next where the two differ — along the thicker one's edge.
    const line: Vec2[] = [];
    boards.forEach((bd, i) => {
      if (i > 0 && Math.abs(A[i] - A[i - 1]) > 0.5) {
        line.push(A[i] > A[i - 1] ? toward(ends[i].b, ends[i].bo, A[i - 1]) : toward(ends[i - 1].f, ends[i - 1].fo, A[i]));
      }
      line.push(bd[1], bd[2]);
    });
    closed[k] = flags[k];
    out({ bench, side, closed: flags[k], boards, poly: [b, ...line, f], line });
  });
  return closed;
}

/** One slat's pieces: a block; the front board carved for an A1 channel (`carve`:
 *  its width) — narrowed by the groove's depth into it over the groove's height
 *  —; turned down at a closed end (`closedSlat`), or, carved, standing on its
 *  turned-down parts (`closureLegs`) — either way its own thickness. */
function slatPieces(poly: Vec2[], base: number, top: number, carve: number | null, close: Closure | null): Solid[] {
  const G = A1_CHANNEL.groove;
  const T = top - base;
  const bottoms: [number | null, number | null] = close && poly.length === 4
    ? [close.start ? close.floor(slatEndPoint(poly, 'start', slatInset(poly, 'start', T) / 2)) : null, close.end ? close.floor(slatEndPoint(poly, 'end', slatInset(poly, 'end', T) / 2)) : null]
    : [null, null];
  if (carve != null && poly.length === 4 && top - base > G.height) {
    const f = Math.min(1, G.intoFront / carve);
    return [
      { part: 'slat', poly, base: base + G.height, top },
      { part: 'slat', poly: [lerp(poly[0], poly[3], f), lerp(poly[1], poly[2], f), poly[2], poly[3]], base, top: base + G.height },
      ...(close ? closureLegs(poly, base, T, bottoms) : []),
    ];
  }
  const turned = close ? closedSlat(poly, base, top, T, bottoms) : null;
  return [turned ?? { part: 'slat', poly, base, top }];
}

/** Walk a polygonal room's benches — its wall bands, rounded-corner pieces and
 *  partition benches — into `sink`, in that order. */
function eachRoomBench(inst: ProductInstance, heights: Pick<ProductTemplate, 'benchTierHeights'>, sink: BenchSink): void {
  const tuck = polyBenchTuckShifts(inst);
  const lifts = polyBenchLifts(inst);
  // A floor grille over the room's floor: what stands on the floor there
  // (a skirting, a closed end) stands on it.
  const floor = grilleTop(inst);
  const bands = polyBenchBands(inst);
  const corners = polyBenchCornerBands(inst);
  const inner = polyInnerBenchBands(inst);
  const view = wallView(inst);
  const edges = polyEdges(view);
  const pbOf = (id: string) => inst.polyBenches?.find((x) => x.id === id);
  // Every seat's footprint and top, for what a skirting stands on.
  const seats = [
    ...bands.map((b) => ({ poly: shiftBandWallward(inst, b, tuck.get(b.id) ?? 0), top: benchHeight({ tier: b.level, height: pbOf(b.id)?.height }, heights) })),
    ...corners.map((c) => ({ poly: c.poly, top: benchHeight({ tier: c.level, height: c.height }, heights) })),
    ...inner.map((b) => ({ poly: b.poly, top: benchHeight({ tier: b.level, height: b.leg.height }, heights) })),
  ];
  const seatUnder = (p: Vec2, top: number): number =>
    Math.max(0, ...seats.filter((s) => s.top < top - 1e-6 && pointInPolygon(p, s.poly)).map((s) => s.top));
  /** The top of a slatted tier's trestle top rail — its frame's underside —, for
   *  the tier behind it to line its mid rail up with; none for a plain or steam bench. */
  const railTop = (b: (SlatSpec & { profile?: unknown }) | undefined, tier: Parameters<typeof benchHeight>[0]): number | undefined =>
    b?.slats?.length && !b.profile ? benchHeight(tier, heights) - seatSlab(b) - FRAME_HEIGHT : undefined;
  // The benches an A1 light runs under: their front board (and closure) carved
  // for its channel, where the front board takes one.
  const a1 = new Set((inst.lights ?? []).flatMap((l) => (l.kind === 'strip' && l.pos === 'A1' && 'bench' in l.host ? [l.host.bench] : [])));
  const grooved = (id: string | undefined, b: SlatSpec | undefined): boolean => !!id && !!b && a1.has(id) && frontTakesChannel(b);
  // A bench's end can be closed — the Bench end tool — unless another bench
  // carries on just beyond it (every seat, the corner pieces'): against the
  // room's walls, a heater wall or a partition it closes too, its boards maybe
  // seen through a glass or an opening.
  const closable = (p: Vec2): boolean => !seats.some((s) => s.poly.length >= 3 && pointInPolygon(p, s.poly));
  /** A slatted run's closed ends — where another bench carries on, none — and,
   *  for the tool, the ends it can close; each slat turns down its own
   *  thickness (`baseOf` its underside). */
  const closure = (id: string, quads: Vec2[][], b: SlatSpec & { closeStart?: boolean; closeEnd?: boolean; skirtingBoards?: SkirtingSpec }, top: number, baseOf: (i: number) => number): Closure | null => {
    const flags = closedFlags(b);
    if (!sink.end && !flags[0] && !flags[1]) return null;
    const [start, end] = runEnds(id, quads, quads.map((_, i) => top - baseOf(i)), flags, closable, sink.end ?? (() => {}));
    return start || end ? { frame: seatSlab(b), start, end, floor: (p) => Math.max(floor, seatUnder(p, top)) + closureClearance(b) } : null;
  };
  /** A run's slats, the front board — the last — carved for an A1 channel:
   *  narrowed by the groove's depth into it over the groove's height; each
   *  turned down at a closed end (`closedSlat`). */
  const slats = (tag: BenchTag, quads: Vec2[][], baseOf: (i: number) => number, top: number, carve: number | null, close: Closure | null): void => {
    quads.forEach((poly, i) => {
      for (const s of slatPieces(poly, baseOf(i), top, carve != null && i === quads.length - 1 ? carve : null, close)) sink.piece({ ...tag, ...s });
    });
  };

  // Each slatted bench's front board's underside, by bench id: a frame run on
  // under it is notched round it, or — where it hangs below that frame —
  // stops with its seat (`stopsUnder`).
  const boardBase = new Map<string, number>();
  for (const b of bands) {
    const pb = pbOf(b.id), f = pb && !pb.profile ? frontBoard(pb) : null;
    if (pb && f) boardBase.set(b.id, benchHeight({ tier: b.level, height: pb.height }, heights) - f.hang);
  }
  for (const b of inner) {
    const f = b.leg.profile ? null : frontBoard(b.leg);
    if (f) boardBase.set(b.id, benchHeight({ tier: b.level, height: b.leg.height }, heights) - f.hang);
  }
  const stopsUnder = (bench: string | undefined, frameBase: number): boolean => {
    const z = bench != null ? boardBase.get(bench) : undefined;
    return z != null && z <= frameBase + 1e-6;
  };
  for (const b of bands) {
    const pb = pbOf(b.id);
    const spec = { tier: b.level, height: pb?.height, thickness: pb?.thickness, slatThickness: pb?.slatThickness };
    const top = benchHeight(spec, heights);
    const tag: BenchTag = { id: b.id, kind: 'band', level: b.level, color: pb?.color };
    const shift = tuck.get(b.id) ?? 0;
    if (pb?.profile) {
      const quad = shiftBandWallward(inst, b, shift);
      if (quad.length === 4) {
        const lift = lifts.get(b.id) ?? 0;
        const width = profileExtent(pb.profile).width;
        sink.piece({ ...tag, part: 'slat', poly: quad, base: 0, top: top + lift, sweep: { profile: profileFilled(pb.profile, lift), pathAt: bandPathAt(quad, width), quad } });
        sink.line?.({ id: b.id, kind: 'band', partition: false, level: b.level, path: bandPathAt(quad, width), depth: width, top: top + lift, profile: pb.profile, lift });
        continue;
      }
    }
    const next = bands.find((c) => c.edge === b.edge && c.tier === b.tier + 1 && c.start < b.end && c.end > b.start);
    // How far the tier in front reaches behind this one's frame's front.
    const tuckUnder = (D: number, F: number): number => next ? (tuck.get(next.id) ?? 0) - shift - (D - F) : 0;
    if (sink.line && pb && b.poly.length >= 4) {
      const D0 = pb.depth > 0 ? pb.depth : 1;
      const at0 = bandAt(b.poly, D0, shift);
      sink.line({ id: b.id, kind: 'band', partition: false, level: b.level, path: (d) => [at0(0, d), at0(1, d)], depth: D0, top, ...benchFrontOf(pb, D0, top, (d) => seatUnder(at0(0.5, d), top), (F) => tuckUnder(D0, F)) });
    }
    const quads = pb?.slats?.length ? polyBenchSlatQuads(b, pb.slats, pb.gaps ?? [], pb.depth, shift) : [];
    const e = edges[b.edge];
    if (!pb || !quads.length || b.poly.length < 4 || !e) {
      sink.piece({ ...tag, part: 'slat', poly: shiftBandWallward(inst, b, shift), base: benchBase(spec, heights), top });
      continue;
    }
    const carveWall = grooved(b.id, pb) ? frontBoard(pb)?.width ?? null : null;
    const baseOf = (i: number): number => slatBase(spec, i, top);
    const close = closure(b.id, quads, pb, top, baseOf);
    slats(tag, quads, baseOf, top, carveWall, close);
    // Its frame is laid from its own quad where a slatted mitre parts it from
    // the seat's; its last stud lies on the mitre where its frame runs through.
    const fq = [...(b.frame ?? b.poly)];
    for (const u of b.under ?? []) {
      if (!stopsUnder(u.bench, top - seatSlab(pb) - FRAME_HEIGHT)) continue;
      for (const k of u.end === 0 ? [0, 3] : [1, 2]) fq[k] = b.poly[k];
    }
    const lo = fq[0];
    const D = pb.depth > 0 ? pb.depth : 1;
    const { at } = closedRun(bandAt(fq, D, shift), D, close);
    const slid = bandAt(fq, D, shift)(0, 0), dx = slid.x - lo.x, dy = slid.y - lo.y;
    const mitres = b.mitres?.map((m) => ({ end: m.end, at: (d: number): Vec2 => { const p = lerp(m.outer, m.inner, d / D); return { x: p.x + dx, y: p.y + dy }; } }));
    const front = frontBoard(pb);
    const F = front ? Math.min(D, front.d) : D;
    const skirt = skirtingOf(pb);
    const runTuck = tuckUnder(D, F);
    const skirtBack = skirt ? runSkirtBack(skirt, runTuck, pb.skirtingSetBack, F) : 0;
    // Square to the wall, by the wall's own station (mm from its first corner),
    // so a support's place reads the same on every tier; a trestle stays inside
    // both of the band's ends over the frame's whole depth.
    const sOf = (p: Vec2): number => (p.x - e.a.x) * e.dir.x + (p.y - e.a.y) * e.dir.y;
    const sLo = sOf(lo);
    const across = (s: number, d: number): Vec2 => ({
      x: lo.x + e.dir.x * (s - sLo) + e.normal.x * (d - shift),
      y: lo.y + e.dir.y * (s - sLo) + e.normal.y * (d - shift),
    });
    // …and under its seat, but where its frame runs through under a mitre: a
    // frame run on under another bench's front board stands no trestle there.
    const seatAt = bandAt(b.poly, D, shift), through = (end: 0 | 1): boolean => !!b.mitres?.some((x) => x.end === end);
    const sMin = Math.max(sOf(at(0, 0)), sOf(at(0, F)), ...(through(0) ? [] : [sOf(seatAt(0, 0)), sOf(seatAt(0, F))]));
    const sMax = Math.min(sOf(at(1, 0)), sOf(at(1, F)), ...(through(1) ? [] : [sOf(seatAt(1, 0)), sOf(seatAt(1, F))]));
    sink.run(tag, {
      strip: (d0, d1) => [at(0, d0), at(1, d0), at(1, d1), at(0, d1)],
      at,
      across,
      trestles: wallSupportsOn(inst, b.edge, b.start, b.end).map((s) => ({ id: s.id, at: trestleCentre(s.at, flushOnTier(s, b.start, b.end, D), sMin, sMax) })),
      ends: [sMin, sMax],
      ...(mitres?.length ? { mitres } : {}),
      support: { kind: 'wall', host: { edge: b.edge }, len: e.len, start: b.start, end: b.end, tier: b.tier, face: shiftBandWallward(inst, b, shift) },
      len: pathLen([at(0, D / 2), at(1, D / 2)]),
      depth: F,
      top,
      slab: seatSlab(pb),
      frontBase: front ? top - front.hang : undefined,
      tuck: runTuck,
      frontRail: next ? railTop(pbOf(next.id), { tier: next.level, height: pbOf(next.id)?.height }) : undefined,
      skirting: skirt,
      skirtingBase: skirt ? seatUnder(at(0.5, F - skirtBack - Math.max(...skirt.thickness) / 2), top) : 0,
      skirtingFloor: floor,
      skirtingSetBack: pb.skirtingSetBack,
      ...(carveWall != null ? { groove: true } : {}),
    });
  }
  // A rounded end's supports are its stack's: stored on the stack's wall-most
  // tier, read by every tier rounded round the same wall point.
  const roundKey = new Map(corners.flatMap((c) => (c.roundEnd ? [[`${c.roundEnd.bench}:${c.roundEnd.side}`, c.roundEnd.key] as const] : [])));
  const roundHost = new Map<string, { roundBench: string; roundSide: 'start' | 'end' }>();
  for (const c of [...corners].sort((a, b) => a.tier - b.tier)) {
    if (c.roundEnd && !roundHost.has(c.roundEnd.key)) roundHost.set(c.roundEnd.key, { roundBench: c.roundEnd.bench, roundSide: c.roundEnd.side });
  }
  for (const c of corners) {
    if (c.poly.length < 3) continue;
    const spec = { tier: c.level, height: c.height, thickness: c.thickness, slatThickness: c.slatThickness };
    const top = benchHeight(spec, heights);
    const tag: BenchTag = { id: c.id, kind: 'corner', level: c.level, color: c.color };
    const src = pbOf(c.sourceId);
    if (src?.profile) {
      const lift = lifts.get(c.sourceId) ?? 0;
      sink.piece({ ...tag, part: 'slat', poly: c.poly, base: 0, top: top + lift, sweep: { profile: profileFilled(src.profile, lift), pathAt: c.facePath } });
      sink.line?.({ id: c.id, kind: 'corner', partition: false, sourceId: c.sourceId, level: c.level, path: c.facePath, depth: Math.abs(c.rOuter - c.rInner), top: top + lift, profile: src.profile, lift });
      continue;
    }
    const nextC = corners.find((x) => x.vertex === c.vertex && x.tier === c.tier + 1 && x.roundEnd?.key === c.roundEnd?.key);
    // How far the tier in front reaches behind this piece's frame's front —
    // only a sector stands on trestles for it to meet.
    const tuckUnder = (D: number, F: number): number => src && nextC && D > 0 && cornerArc(c, F) ? F - ((nextC.rOuter - c.rOuter) / (c.rInner - c.rOuter)) * D : 0;
    if (sink.line && src) {
      const Dc = Math.abs(c.rOuter - c.rInner);
      const mid = (d: number): Vec2 => { const pts = c.facePath(d); return pts[Math.floor(pts.length / 2)]; };
      sink.line({ id: c.id, kind: 'corner', partition: false, sourceId: c.sourceId, level: c.level, path: c.facePath, depth: Dc, top, ...benchFrontOf(c.skirting ? src : { ...src, skirting: false }, Dc, top, (d) => seatUnder(mid(d), top), (F) => tuckUnder(Dc, F)) });
    }
    const quads = polyBenchCornerSlatQuads(inst, c);
    if (!quads.length) { sink.piece({ ...tag, part: 'slat', poly: c.poly, base: benchBase(spec, heights), top }); continue; }
    // Concentric slats continue the bench's own, one for one; radial wedges are
    // all alike, so they take the bench's thickness.
    const radial = view.polygonCornerSlats?.[c.vertex] === 'radial';
    const radialBase = Math.max(0, top - (src ? seatSlab(src) : benchThickness(spec) ?? DEFAULT_SLAT_THICKNESS));
    const D = Math.abs(c.rOuter - c.rInner);
    const front = src ? frontBoard(src) : null;
    const F = front ? Math.min(D, front.d) : D;
    // Concentric, its front board carved round the arc as the bench's is.
    const carveCorner = !radial && front && grooved(c.sourceId, src) && quads.length;
    quads.forEach((poly, i) => {
      const base = radial ? radialBase : slatBase(spec, i, top), G = A1_CHANNEL.groove;
      if (carveCorner && i === quads.length - 1 && top - base > G.height) {
        sink.piece({ ...tag, part: 'slat', poly, base: base + G.height, top });
        sink.piece({ ...tag, part: 'slat', poly: [...c.facePath(F + Math.min(front!.width, G.intoFront)), ...[...c.facePath(F + front!.width)].reverse()], base, top: base + G.height });
      } else sink.piece({ ...tag, part: 'slat', poly, base, top });
    });
    const skirt = c.skirting && src ? skirtingOf(src) : null;
    const runTuck = tuckUnder(D, F);
    const skirtBack = skirt ? runSkirtBack(skirt, runTuck, src?.skirtingSetBack, F) : 0;
    const frontPath = c.facePath(F - skirtBack - (skirt ? Math.max(...skirt.thickness) : 0) / 2);
    // A pure sector (both faces curve) stands on trestles laid radially, where
    // supports are placed round its corner; the tier in front, its own sector.
    const arc = src ? cornerArc(c, F) : null;
    const next = nextC;
    sink.run(tag, {
      strip: (d0, d1) => [...c.facePath(d0), ...[...c.facePath(d1)].reverse()],
      ...(arc ? {
        across: arc.across,
        trestleAt: arc.trestleAt,
        stationOf: arc.stationOf,
        trestles: (c.roundEnd ? roundSupportsOn(inst, c.roundEnd.key, (bench, side) => roundKey.get(`${bench}:${side}`)) : cornerSupportsOn(inst, c.vertex)).map((s) => ({ id: s.id, at: trestleCentre(runStation(s, arc.len), s.flush, arc.lo, arc.len - arc.lo) })),
        ends: [arc.lo, arc.len - arc.lo] as [number, number],
        support: { kind: 'corner' as const, host: c.roundEnd ? roundHost.get(c.roundEnd.key)! : { corner: c.vertex }, len: arc.len, start: 0, end: arc.len, tier: c.tier, face: c.poly },
        tuck: runTuck,
        frontRail: next ? railTop(pbOf(next.sourceId), { tier: next.level, height: next.height }) : undefined,
      } : {}),
      len: arc ? arc.len : pathLen(c.facePath(F / 2)),
      depth: F,
      top,
      slab: src ? seatSlab(src) : benchThickness(spec) ?? DEFAULT_SLAT_THICKNESS,
      frontBase: front ? top - front.hang : undefined,
      skirting: skirt,
      skirtingBase: skirt ? seatUnder(frontPath[Math.floor(frontPath.length / 2)], top) : 0,
      skirtingFloor: floor,
      skirtingSetBack: src?.skirtingSetBack,
      ...(carveCorner ? { groove: true } : {}),
    });
  }
  // Partition benches: the run is the partition's inward length, the depth out
  // from its face (`PolyInnerBenchBand.at`), built as a wall bench is.
  for (const b of inner) {
    const leg = b.leg;
    const spec = { tier: b.level, height: leg.height, thickness: leg.thickness, slatThickness: leg.slatThickness };
    const top = benchHeight(spec, heights);
    const tag: BenchTag = { id: b.id, kind: 'band', level: b.level, color: leg.color, partition: true };
    if (leg.profile) {
      const width = profileExtent(leg.profile).width;
      sink.piece({ ...tag, part: 'slat', poly: b.poly, base: 0, top: top + b.lift, sweep: { profile: profileFilled(leg.profile, b.lift), pathAt: bandPathAt(b.sweep, width), quad: b.sweep } });
      sink.line?.({ id: b.id, kind: 'band', partition: true, level: b.level, path: bandPathAt(b.sweep, width), depth: width, top: top + b.lift, profile: leg.profile, lift: b.lift });
      continue;
    }
    const next = inner.find((c) => c.iwIndex === b.iwIndex && c.face === b.face && c.index === b.index + 1);
    // How far the tier in front reaches behind this one's frame's front.
    const tuckUnder = (D: number, F: number): number => next ? next.tuck - b.tuck - (D - F) : 0;
    if (sink.line) {
      const Lb = b.len, Db = leg.depth > 0 ? leg.depth : 1;
      sink.line({ id: b.id, kind: 'band', partition: true, level: b.level, path: (d) => [b.at(b.seatStart?.(d) ?? 0, d), b.at(Lb, d)], depth: Db, top, ...benchFrontOf(leg, Db, top, (d) => seatUnder(b.at(Lb / 2, d), top), (F) => tuckUnder(Db, F)) });
    }
    const quads = polyInnerBenchSlatQuads(b);
    if (!quads.length) {
      sink.piece({ ...tag, part: 'slat', poly: b.poly, base: benchBase(spec, heights), top });
      continue;
    }
    const L = b.len;
    const carveLeg = grooved(leg.id, leg) ? frontBoard(leg)?.width ?? null : null;
    const baseOf = (i: number): number => slatBase(spec, i, top);
    const close = closure(b.id, quads, leg, top, baseOf);
    slats(tag, quads, baseOf, top, carveLeg, close);
    // Its frame starts where the partition band's does, or with its seat where
    // the wall bench's board it would run on under hangs below it.
    const k0 = b.under && b.seatStart && stopsUnder(b.under, top - seatSlab(leg) - FRAME_HEIGHT) ? b.seatStart(0) : 0;
    const Lr = L - k0;
    const { at, t0: c0, t1 } = closedRun((u, d) => b.at(k0 + u * Lr, d), leg.depth > 0 ? leg.depth : 1, close);
    // Its trestles stand under its seat, where its frame runs on past it.
    const t0 = Math.max(k0 + c0, b.seatStart && !b.mitre ? Math.max(b.seatStart(0), b.seatStart(frontBoard(leg)?.d ?? leg.depth)) : 0);
    const strip = (d0: number, d1: number): Vec2[] => [at(0, d0), at(1, d0), at(1, d1), at(0, d1)];
    const D = leg.depth > 0 ? leg.depth : 1;
    const front = frontBoard(leg);
    const F = front ? Math.min(D, front.d) : D;
    const skirt = skirtingOf(leg);
    const runTuck = tuckUnder(D, F);
    const skirtBack = skirt ? runSkirtBack(skirt, runTuck, leg.skirtingSetBack, F) : 0;
    const iwId = inst.innerWalls?.[b.iwIndex]?.id;
    sink.run(tag, {
      strip,
      at,
      across: b.at,
      trestles: partitionSupportsOn(inst, iwId, b.face).map((s) => ({ id: s.id, at: trestleCentre(runStation(s, L), s.flush, t0, L - t1) })),
      ends: [t0, L - t1],
      ...(b.mitre ? { mitres: [{ end: 0 as const, at: (d: number) => lerp(b.mitre!.outer, b.mitre!.inner, d / D) }] } : {}),
      ...(iwId ? { support: { kind: 'partition' as const, host: { innerWallId: iwId, face: b.face }, len: L, start: 0, end: L, tier: b.index, face: b.poly } } : {}),
      len: Lr - c0 - t1,
      depth: F,
      top,
      slab: seatSlab(leg),
      frontBase: front ? top - front.hang : undefined,
      tuck: runTuck,
      frontRail: next ? railTop(next.leg, { tier: next.level, height: next.leg.height }) : undefined,
      skirting: skirt,
      skirtingBase: skirt ? seatUnder(b.at(L / 2, F - skirtBack - Math.max(...skirt.thickness) / 2), top) : 0,
      skirtingFloor: floor,
      skirtingSetBack: leg.skirtingSetBack,
      ...(carveLeg != null ? { groove: true } : {}),
    });
  }
}

/** A rounded corner piece whose faces both curve (a sector of rings round its
 *  centre), as a run its trestles stand on: stations round the arc at the frame's
 *  middle (`F` / 2 deep), each trestle laid radially — square to the arc, straight
 *  across its 40 mm —, and the margin `lo` at both ends that keeps a whole
 *  trestle inside the sector at the frame's tightest radius. Null when a face is
 *  a sharp mitre (a radius smaller than the bench is deep). */
function cornerArc(c: PolyBenchCornerBand, F: number): {
  len: number; lo: number; across: (s: number, d: number) => Vec2; trestleAt: (at: number) => (x: number, d: number) => Vec2; stationOf: (p: Vec2) => number;
} | null {
  const D = Math.abs(c.rOuter - c.rInner);
  // A rounded end's wall tier reaches its pivot (radius 0): its trestles still
  // stand radially, kept inside the round at mid depth.
  const fromPivot = !!c.roundEnd && !(c.rOuter > RADIUS_EPS);
  if (!((c.rOuter > RADIUS_EPS || fromPivot) && c.rInner > RADIUS_EPS) || D < 1e-6) return null;
  const r = (d: number): number => c.rOuter + ((c.rInner - c.rOuter) * d) / D;
  // Its angles read off a face of some radius: a rounded end's wall tier's
  // back face is its pivot.
  const C = c.center, wall = c.facePath(fromPivot ? D : 0);
  const angle = (p: Vec2): number => Math.atan2(p.y - C.y, p.x - C.x);
  const wrap = (a: number): number => { while (a > Math.PI) a -= 2 * Math.PI; while (a <= -Math.PI) a += 2 * Math.PI; return a; };
  const t0 = angle(wall[0]), sweep = wrap(angle(wall[wall.length - 1]) - t0), sg = sweep < 0 ? -1 : 1;
  const rm = r(F / 2), rMin = fromPivot ? Math.max(Math.min(r(0), r(F)), rm) : Math.min(r(0), r(F));
  if (!(rMin > TRESTLE_WIDTH / 2)) return null;
  const th = (s: number): number => t0 + (sg * s) / rm;
  const h = TRESTLE_WIDTH / 2;
  return {
    len: Math.abs(sweep) * rm,
    lo: (h * rm) / rMin - h,
    across: (s, d) => { const t = th(s); return { x: C.x + Math.cos(t) * r(d), y: C.y + Math.sin(t) * r(d) }; },
    trestleAt: (at) => {
      const t = th(at), u = { x: Math.cos(t), y: Math.sin(t) }, v = { x: -sg * u.y, y: sg * u.x };
      return (x, d) => ({ x: C.x + u.x * r(d) + v.x * x, y: C.y + u.y * r(d) + v.y * x });
    },
    stationOf: (p) => sg * wrap(angle(p) - t0) * rm,
  };
}

/** A bench's front board and skirting as its line reads them: the board's back
 *  face and hang (none on a bench of fewer than two boards: a plain bench gives
 *  its slab instead), the skirting's front face — set back as its structure
 *  stands it (`skirtingBack`, the tier in front's tuck by `tuckAt` its frame's
 *  front) — and what it stands on, by `under` at a depth. */
function benchFrontOf(b: SlatSpec & { skirting?: boolean; skirtingBoards?: SkirtingSpec; skirtingSetBack?: number }, D: number, top: number, under: (d: number) => number, tuckAt: (F: number) => number): Pick<BenchLine, 'front' | 'skirting' | 'slab'> {
  const front = frontBoard(b);
  const F = front ? Math.min(D, front.d) : D;
  const skirt = skirtingOf(b);
  const out: Pick<BenchLine, 'front' | 'skirting' | 'slab'> = {};
  if (front) out.front = { d: F, hang: front.hang, ...(front.hang > seatSlab(b) + 1 ? { channel: true } : {}) };
  else { const t = benchThickness(b); if (t != null) out.slab = t; }
  if (skirt) {
    const t = Math.max(...skirt.thickness);
    const back = runSkirtBack(skirt, tuckAt(F), b.skirtingSetBack, F);
    out.skirting = { d: F - back, base: under(F - back - t / 2) };
  }
  return out;
}

/** Every bench of a polygonal room as its lights read it — its wall bands,
 *  rounded corner pieces and partition benches, in that order (room local). */
export function roomBenchLines(inst: ProductInstance, heights: Pick<ProductTemplate, 'benchTierHeights'> = { benchTierHeights: inst.benchTierHeights }): BenchLine[] {
  const out: BenchLine[] = [];
  if (isPolyRoom(inst)) eachRoomBench(inst, heights, { piece: () => {}, run: () => {}, line: (l) => out.push(l) });
  return out;
}

/**
 * Every solid piece of a polygonal room's benches — its wall bands, rounded
 * corner pieces and partition benches — in the room's local frame. A slatted
 * bench gives one piece per slat, each hanging its own thickness from the seat
 * ({@link slatBase}), then its structure ({@link benchStructure}); a bench with
 * no slats is one piece from {@link benchBase} to its seat, and a steam bench
 * its profile's sweep.
 */
export function roomBenchSolids(inst: ProductInstance, heights: Pick<ProductTemplate, 'benchTierHeights'>): BenchSolid[] {
  const out: BenchSolid[] = [];
  eachRoomBench(inst, heights, {
    piece: (s) => out.push(s),
    run: (tag, run) => { for (const s of benchStructure(run)) out.push({ ...tag, ...s }); },
  });
  return carveSolids(inst, notchSeats(notchUnderBoards(out)));
}

/** Axis-aligned bounds of a point list. */
function bounds(pts: readonly Vec2[]): { x0: number; y0: number; x1: number; y1: number } {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of pts) { if (p.x < x0) x0 = p.x; if (p.x > x1) x1 = p.x; if (p.y < y0) y0 = p.y; if (p.y > y1) y1 = p.y; }
  return { x0, y0, x1, y1 };
}

/**
 * A bench piece's plan polygon (room local) less what its room's contents carve
 * — `room.benchHoles`, each one's true (turned) footprint: a heater's with its
 * gap and cover, a steam cube's (roomRegen.ts `heaterHoles`) — then less any
 * `clips` (a wall bench's partitions, `partitionClips`): the rings that remain,
 * outer + holes. The ONE carve: `roomBenchSolids` (section, 3D) cuts every piece
 * with it, `roomBenchSeats` the seats the plan and the DXF draw.
 * Nothing reaching it: the polygon itself, untouched.
 */
export function carvedSeat(room: Pick<ProductInstance, 'benchHoles'>, poly: Vec2[], clips: readonly Vec2[][] = []): RingPoly[] {
  if (poly.length < 3) return [];
  const holes = room.benchHoles ?? [];
  if (!holes.length && !clips.length) return [{ outer: poly, holes: [] }];
  const b = bounds(poly);
  let rings: RingPoly[] = [{ outer: poly, holes: [] }];
  for (const cut of [...holes, ...clips]) {
    if (cut.length < 3) continue;
    const c = bounds(cut);
    if (c.x1 <= b.x0 || c.x0 >= b.x1 || c.y1 <= b.y0 || c.y0 >= b.y1) continue; // disjoint
    rings = rings.flatMap((r) => polygonBoolean(r, { outer: cut, holes: [] }, 'difference'));
    if (!rings.length) return rings;
  }
  return rings;
}

/** A partition's bench as the plan and the DXF draw it (`roomBenchSeats`): its
 *  slats — its band when it has none — each less what stands in the room
 *  (`carvedSeat`), the carve `roomBenchSolids` gives the section and the 3D
 *  view. The partitions do not cut it (`partitionClips` cut the wall benches). */
export function partitionBenchSeat(inst: Pick<ProductInstance, 'benchHoles'>, b: PolyInnerBenchBand): RingPoly[] {
  const slats = polyInnerBenchSlatQuads(b);
  return (slats.length ? slats : [b.poly]).flatMap((q) => carvedSeat(inst, q));
}

/** A room's bench as the plan draws its seat ({@link roomBenchSeats}). */
export interface BenchSeat {
  /** A wall's or a partition's bench id, or a rounded corner piece's: the id
   *  its trestles carry. */
  id: string;
  kind: 'band' | 'corner';
  partition: boolean;
  level: BenchTier;
  /** Its own colour; none, the room's. */
  color?: string;
  /** What the plan fills it with: its own colour, else the room's bench
   *  colour, else its tier's. Its marks are shades of it. */
  fill: string;
  /** Drawn as its slats, its trestles seen in the gaps. */
  slatted: boolean;
  /** Its slats — its band when it has none —, tucked, each less what stands in
   *  the room and, on a wall, the partitions (`carvedSeat`). */
  rings: RingPoly[];
  /** A slatted seat's trestles (`benchSupportMarks`): filled in
   *  `structureColor(fill)` under its slats, outlined dashed in
   *  `TRESTLE_LINE_SHADE` over every seat. */
  trestles: TrestleMark[];
  /** An unslatted wall band's front edge, tucked: its lip, a line in
   *  `LIP_SHADE` over its outline so the seat reads as raised. */
  lip?: [Vec2, Vec2];
}

/** How much darker than a seat's fill the plan draws its lip and its trestles'
 *  dashed outline (`darkenHex`). */
export const LIP_SHADE = 0.25;
export const TRESTLE_LINE_SHADE = 0.4;
/** A closed end's line across its bench ({@link closedEndLines}). */
export const BENCH_END_LINE = '#4a3a28';

/**
 * Every bench seat of a polygonal room as the plan draws it, in its paint
 * order: the wall benches by `benchDrawOrder` (the tier furthest from the wall
 * first, each rounded corner piece at its own tier), each slid wall-ward by its
 * tuck (`polyBenchTuckShifts`), its slats (`polyBenchSlatQuads`, a corner's
 * `polyBenchCornerSlatQuads`) else its band, carved and cut at the partitions
 * (`partitionClips`, with `pending`, a partition being drawn); then the
 * partitions' benches by `innerBenchPaintOrder`, through `partitionBenchSeat`.
 * Each carries its marks: a slatted seat its trestles, a plain wall band its
 * lip. The ONE list the plan (render.ts) and the DXF (dxfExport.ts) write:
 * each seat (its trestles' fill under it, its lip over it), then every seat's
 * trestle outlines, then the closed ends' lines ({@link closedEndLines}).
 */
export function roomBenchSeats(inst: ProductInstance, pending?: InnerWall | null): BenchSeat[] {
  if (!isPolyRoom(inst)) return [];
  const clips = partitionClips(inst, pending);
  const tuck = polyBenchTuckShifts(inst);
  const marks = benchSupportMarks(inst);
  const carve = (quads: Vec2[][]): RingPoly[] => quads.flatMap((q) => carvedSeat(inst, q, clips));
  const fill = (color: string | undefined, level: BenchTier): string =>
    color ?? (inst.benchColor || (inst.category === 'steamroom' ? STEAM_BENCH_TIER_COLORS : BENCH_TIER_COLORS)[level]);
  const marked = (id: string, slatted: boolean): Pick<BenchSeat, 'slatted' | 'trestles'> => ({ slatted, trestles: slatted ? marks.filter((m) => m.bench === id) : [] });
  const out: BenchSeat[] = [];
  for (const item of benchDrawOrder(inst)) {
    if (item.kind === 'corner') {
      const c = item.corner;
      if (c.poly.length < 3) continue;
      const slats = polyBenchCornerSlatQuads(inst, c);
      out.push({ id: c.id, kind: 'corner', partition: false, level: c.level, color: c.color, fill: fill(c.color, c.level), ...marked(c.id, slats.length > 0), rings: carve(slats.length ? slats : [c.poly]) });
      continue;
    }
    const b = item.band, pb = inst.polyBenches?.find((p) => p.id === b.id), shift = tuck.get(b.id) ?? 0;
    const seat = { id: b.id, kind: 'band' as const, partition: false, level: b.level, color: pb?.color, fill: fill(pb?.color, b.level) };
    const slats = pb?.slats?.length ? polyBenchSlatQuads(b, pb.slats, pb.gaps ?? [], pb.depth, shift) : [];
    if (slats.length) { out.push({ ...seat, ...marked(b.id, true), rings: carve(slats) }); continue; }
    const band = shiftBandWallward(inst, b, shift);
    if (band.length < 3) continue;
    const [l0, l1] = shiftBandWallward(inst, { ...b, poly: b.lip }, shift);
    out.push({ ...seat, ...marked(b.id, false), rings: carve([band]), lip: [l0, l1] });
  }
  for (const b of innerBenchPaintOrder(polyInnerBenchBands(inst))) {
    const color = b.leg.color;
    out.push({ id: b.id, kind: 'band', partition: true, level: b.level, color, fill: fill(color, b.level), ...marked(b.id, polyInnerBenchSlatQuads(b).length > 0), rings: partitionBenchSeat(inst, b) });
  }
  return out;
}

/** A room's (or a standalone bench's) closed ends as the plan draws them: a
 *  line across each at its boards' inside face (`BenchEndMark.line`, room
 *  frame; world for a standalone bench), over the benches and their trestles,
 *  in {@link BENCH_END_LINE}. An open end has none. */
export function closedEndLines(inst: ProductInstance): Vec2[][] {
  if (!hasClosedEnds(inst)) return [];
  return benchEndMarks(inst).filter((m) => m.closed).map((m) => m.line);
}

/** Every piece carved by what stands in its room: a piece cut through splits,
 *  one with a part inside it keeps it as a hole; a swept one as `carveSweep`. */
function carveSolids<T extends Solid>(inst: ProductInstance, solids: T[]): T[] {
  if (!inst.benchHoles?.length) return solids;
  return solids.flatMap((s) => {
    if (s.sweep) return carveSweep(inst, s);
    const rings = carvedSeat(inst, s.poly);
    if (rings.length === 1 && rings[0].outer === s.poly) return [s];
    return rings.map((r) => ({ ...s, poly: r.outer, ...(r.holes.length ? { holes: r.holes } : {}) }));
  });
}

/** A straight swept band (a steam profile, `sweep.quad`) cut through, back to
 *  front, by what stands in its room: one sweep per piece left, its ends along
 *  the cut. A part inside it or biting into one edge, or a corner piece or a
 *  closed end (no quad), leaves it whole: no sweep carries that. */
function carveSweep<T extends Solid>(inst: ProductInstance, s: T): T[] {
  const sw = s.sweep!, q = sw.quad;
  if (!q || q.length !== 4) return [s];
  const rings = carvedSeat(inst, q);
  if (rings.length === 1 && rings[0].outer === q) return [s];
  const bands = rings.map((r) => (r.holes.length ? null : subBand(q, r.outer)));
  if (!bands.length || bands.some((b) => !b)) return [s];
  // The depth x of the sweep as a fraction of the band's, read off its own path.
  const dx = q[3].x - q[0].x, dy = q[3].y - q[0].y, dd = dx * dx + dy * dy;
  const frac = (x: number): number => {
    const p = sw.pathAt(x)[0];
    return dd > 0 ? ((p.x - q[0].x) * dx + (p.y - q[0].y) * dy) / dd : 0;
  };
  const lerp = (a: Vec2, b: Vec2, t: number): Vec2 => ({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t });
  return (bands as Vec2[][]).map((b) => ({
    ...s, poly: b,
    sweep: { ...sw, quad: b, pathAt: (x: number) => { const t = frac(x); return [lerp(b[0], b[3], t), lerp(b[1], b[2], t)]; } },
  }));
}

/** A carved ring of the band `q` (`[backStart, backEnd, frontEnd, frontStart]`)
 *  that still runs from its back edge to its front edge, in that order; else null. */
function subBand(q: readonly Vec2[], ring: readonly Vec2[]): Vec2[] | null {
  const line = (a: Vec2, b: Vec2) => {
    const l = Math.hypot(b.x - a.x, b.y - a.y) || 1;
    return (p: Vec2): number => Math.abs((p.x - a.x) * (b.y - a.y) - (p.y - a.y) * (b.x - a.x)) / l;
  };
  // Corners only: a vertex polybool leaves on a straight run is not one.
  const pts = ring.filter((p, i) => {
    const a = ring[(i - 1 + ring.length) % ring.length], b = ring[(i + 1) % ring.length];
    return line(a, b)(p) > 1e-6;
  });
  if (pts.length !== 4) return null;
  const toBack = line(q[0], q[1]), toFront = line(q[3], q[2]);
  const back = pts.filter((p) => toBack(p) < 1e-3), front = pts.filter((p) => toFront(p) < 1e-3);
  if (back.length !== 2 || front.length !== 2) return null;
  const ux = q[1].x - q[0].x, uy = q[1].y - q[0].y;
  const along = (a: Vec2, b: Vec2): number => (a.x - b.x) * ux + (a.y - b.y) * uy;
  back.sort(along);
  front.sort(along);
  return [back[0], back[1], front[1], front[0]];
}

/** A frame or skirting running on under another bench's board that hangs below
 *  its top — the loser's frame under the winner's front board at a corner —
 *  is notched round it: the part under the board lowered to the board's
 *  underside (gone where that leaves nothing), the rest whole. */
function notchUnderBoards(solids: BenchSolid[]): BenchSolid[] {
  const boards = solids.filter((s) => s.part === 'slat' && !s.sweep && s.poly.length === 4);
  return solids.flatMap((s) => {
    if ((s.part !== 'frame' && s.part !== 'skirting') || s.sweep) return [s];
    let pieces: BenchSolid[] = [s];
    for (const b of boards) {
      if (b.id === s.id || b.base >= s.top - 1e-6 || b.top <= s.base + 1e-6) continue;
      pieces = pieces.flatMap((p) => {
        if (p.top <= b.base + 1e-6) return [p];
        const { inside, outside } = splitConvex(p.poly, b.poly);
        if (!inside.length) return [p];
        const under = b.base - p.base > 1e-6 ? [{ ...p, poly: inside, top: b.base }] : [];
        return [...outside.map((poly) => ({ ...p, poly })), ...under];
      });
    }
    return pieces;
  });
}

/** A skirting batten standing on the seat below reaches down into it: each slat
 *  it stands in is notched round it, to the batten's foot (cavalletto.dxf). A
 *  slat not square to the batten — a mitred end, a corner piece — is left whole. */
function notchSeats(solids: BenchSolid[]): BenchSolid[] {
  const studs = solids.filter((s) => s.part === 'frame' && s.sweep?.quad);
  if (!studs.length) return solids;
  return solids.flatMap((s) => {
    if (s.part !== 'slat' || s.sweep) return [s];
    let pieces: BenchSolid[] = [s];
    for (const b of studs) {
      if (b.base > s.base + 1e-6 && b.base < s.top - 1e-6) pieces = pieces.flatMap((p) => (p.top < s.top ? [p] : notchPiece(p, b.sweep!.quad!, b.base)));
    }
    return pieces;
  });
}

/** `p` with the part under the rectangle `q` ([backStart, backEnd, frontEnd,
 *  frontStart]) lowered to `z`: the rest whole round it. */
function notchPiece(p: BenchSolid, q: Vec2[], z: number): BenchSolid[] {
  const o = q[0], lx = Math.hypot(q[1].x - o.x, q[1].y - o.y), ly = Math.hypot(q[3].x - o.x, q[3].y - o.y);
  if (p.poly.length !== 4 || lx < 1e-6 || ly < 1e-6) return [p];
  const ux = { x: (q[1].x - o.x) / lx, y: (q[1].y - o.y) / lx }, uy = { x: (q[3].x - o.x) / ly, y: (q[3].y - o.y) / ly };
  const loc = p.poly.map((c) => ({ x: (c.x - o.x) * ux.x + (c.y - o.y) * ux.y, y: (c.x - o.x) * uy.x + (c.y - o.y) * uy.y }));
  const xs = loc.map((c) => c.x), ys = loc.map((c) => c.y);
  const [sx0, sx1, sy0, sy1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const square = loc.every((c) => (Math.abs(c.x - sx0) < 0.01 || Math.abs(c.x - sx1) < 0.01) && (Math.abs(c.y - sy0) < 0.01 || Math.abs(c.y - sy1) < 0.01));
  const ix0 = Math.max(sx0, 0), ix1 = Math.min(sx1, lx), iy0 = Math.max(sy0, 0), iy1 = Math.min(sy1, ly);
  if (!square || ix1 - ix0 < 0.5 || iy1 - iy0 < 0.5) return [p];
  const rect = (x0: number, x1: number, y0: number, y1: number): Vec2[] =>
    [[x0, y0], [x1, y0], [x1, y1], [x0, y1]].map(([x, y]) => ({ x: o.x + ux.x * x + uy.x * y, y: o.y + ux.y * x + uy.y * y }));
  const out: BenchSolid[] = [];
  const keep = (x0: number, x1: number, y0: number, y1: number, top: number): void => {
    if (x1 - x0 > 0.01 && y1 - y0 > 0.01 && top - p.base > 1e-6) out.push({ ...p, poly: rect(x0, x1, y0, y1), top });
  };
  keep(sx0, ix0, sy0, sy1, p.top);
  keep(ix1, sx1, sy0, sy1, p.top);
  keep(ix0, ix1, sy0, iy0, p.top);
  keep(ix0, ix1, iy1, sy1, p.top);
  keep(ix0, ix1, iy0, iy1, z);
  return out;
}

/** One trestle on the plan: its footprint (a room's local frame; world for a
 *  standalone bench), the support it stands for and the bench it is under. `run`
 *  numbers the run it stands on (a bench band, a rounded corner piece — measured
 *  round its arc —, a standalone bench's segment), `at` is its centre along it
 *  and `ends` the run's ends, both in the run's own station (mm), and `point(s)`
 *  the plan point at station `s` on the trestles' middle line. */
export interface TrestleMark {
  support: string;
  bench: string;
  poly: Vec2[];
  run: number;
  at: number;
  ends: [number, number];
  point: (s: number) => Vec2;
  /** The run it stands on, as a support is moved along it. */
  sr: SupportRun | null;
}

/** Where every placed support's trestles stand on the plan, one under each tier
 *  of bench it spans. `extra` adds supports not (yet) placed — the tool's
 *  preview of what a click would add. */
export function benchSupportMarks(inst: ProductInstance, heights: Pick<ProductTemplate, 'benchTierHeights'> = { benchTierHeights: inst.benchTierHeights }, extra: BenchSupport[] = []): TrestleMark[] {
  const host = extra.length ? { ...inst, benchSupports: [...(inst.benchSupports ?? []), ...extra] } : inst;
  if (!host.benchSupports?.length) return [];
  const out: TrestleMark[] = [];
  let runs = 0;
  const mark = (bench: string, run: BenchRun): void => {
    const k = runs++;
    const across = run.across;
    if (!across) return;
    const mid = Math.max(TRESTLE_TIMBER, trestleReach(run)) / 2;
    const sr = supportRunOf(run);
    for (const t of run.trestles ?? []) {
      const poly = trestleFootprint(run, t.at);
      if (poly) out.push({ support: t.id, bench, poly, run: k, at: t.at, ends: run.ends ?? [0, run.len], point: (s) => across(s, mid), sr });
    }
  };
  if (host.category === 'saunabench') eachNodeBench(host, { piece: () => {}, run: (tag, run) => mark(tag.id, run) });
  else eachRoomBench(host, heights, { piece: () => {}, run: (tag, run) => mark(tag.id, run) });
  return out;
}

/** The run a support moves along, from the bench run itself: its host, its
 *  usable stretch and a point's station (projected square to it, clamped). */
function supportRunOf(run: BenchRun): SupportRun | null {
  const info = run.support, across = run.across;
  if (!info || !across) return null;
  const o = across(0, 0), x = across(1000, 0);
  const u = { x: (x.x - o.x) / 1000, y: (x.y - o.y) / 1000 };
  const along = run.stationOf ?? ((p: Vec2) => (p.x - o.x) * u.x + (p.y - o.y) * u.y);
  const [lo, hi] = run.ends ?? [0, run.len];
  return {
    kind: info.kind, host: info.host, len: info.len, start: info.start, end: info.end, lo, hi,
    stationOf: (p) => Math.min(hi, Math.max(lo, along(p))),
  };
}

/** Every slatted bench run supports can stand on — a room's wall and partition
 *  bands and its rounded corner pieces (room frame), a standalone bench's
 *  segments (world) — with its bench id, its run number (as `TrestleMark.run`)
 *  and its footprint as painted, in paint order (the last is on top: a
 *  partition's over the walls', a tier against its wall over the ones it tucks
 *  under). A steam profile, a plain block and a corner piece whose front face is
 *  a sharp mitre take none. */
export function supportRuns(inst: ProductInstance, heights: Pick<ProductTemplate, 'benchTierHeights'> = { benchTierHeights: inst.benchTierHeights }): Array<{ bench: string; run: number; face: Vec2[]; sr: SupportRun }> {
  const out: Array<{ bench: string; run: number; face: Vec2[]; sr: SupportRun; key: [number, number] }> = [];
  let runs = 0;
  const take = (bench: string, run: BenchRun): void => {
    const k = runs++;
    const sr = supportRunOf(run);
    if (sr && run.support) out.push({ bench, run: k, face: run.support.face, sr, key: [sr.kind === 'partition' ? 1 : 0, -run.support.tier] });
  };
  if (inst.category === 'saunabench') eachNodeBench(inst, { piece: () => {}, run: (tag, run) => take(tag.id, run) });
  else if (isPolyRoom(inst)) eachRoomBench(inst, heights, { piece: () => {}, run: (tag, run) => take(tag.id, run) });
  return out.sort((a, b) => a.key[0] - b.key[0] || a.key[1] - b.key[1]).map(({ key: _key, ...r }) => r);
}

/** The run a support could be placed on under `p` (a room's frame; world for a
 *  standalone bench): the topmost painted there, or null. */
export function supportRunAt(inst: ProductInstance, p: Vec2): { bench: string; run: number; sr: SupportRun } | null {
  const hit = [...supportRuns(inst)].reverse().find((r) => pointInPolygon(p, r.face));
  return hit ? { bench: hit.bench, run: hit.run, sr: hit.sr } : null;
}

/** A run as a support remembers it: its bench and host — stable across an edit
 *  that lengthens or shortens it, where its index is not. */
const runKey = (bench: string, sr: SupportRun): string => `${bench}|${JSON.stringify(sr.host)}`;

/** Where each support stands on each run it stands on, as a share `f` of the
 *  stretch its trestle centres may take (0 flush with the start, 1 with the
 *  end), and that run's ends: what `keepSupportsRelative` holds them to. */
export type SupportPlaces = Map<string, Array<{ key: string; lo: number; hi: number; f: number }>>;

export function supportPlaces(inst: ProductInstance, heights: Pick<ProductTemplate, 'benchTierHeights'> = { benchTierHeights: inst.benchTierHeights }): SupportPlaces {
  const out: SupportPlaces = new Map();
  const h = TRESTLE_WIDTH / 2;
  for (const m of benchSupportMarks(inst, heights)) {
    if (!m.sr) continue;
    const a = m.sr.lo + h, b = Math.max(a, m.sr.hi - h);
    const f = b - a > 1e-6 ? Math.min(1, Math.max(0, (m.at - a) / (b - a))) : 0.5;
    const list = out.get(m.support) ?? [];
    list.push({ key: runKey(m.bench, m.sr), lo: m.sr.lo, hi: m.sr.hi, f });
    out.set(m.support, list);
  }
  return out;
}

/**
 * A support keeps its place along its bench as the bench changes length (a room
 * resized, a bench shortened, a corner's mitre moved): each support standing on a
 * run whose ends moved since `before` (`supportPlaces`) goes back to the same
 * share of it — flush with an end it was flush with, the middle one in the
 * middle, thirds still thirds. Standing on several runs (a tier each) and more
 * than one changed, the one it is flush with leads, else the first. A support on
 * runs that did not change — slid, retyped, added — is left alone. True when any
 * moved.
 */
export function keepSupportsRelative(inst: ProductInstance, before: SupportPlaces, heights: Pick<ProductTemplate, 'benchTierHeights'> = { benchTierHeights: inst.benchTierHeights }): boolean {
  if (!inst.benchSupports?.length || !before.size) return false;
  const runs = new Map(supportRuns(inst, heights).map((r) => [runKey(r.bench, r.sr), r.sr]));
  const h = TRESTLE_WIDTH / 2;
  let changed = false;
  for (const s of inst.benchSupports) {
    const moved = (before.get(s.id) ?? []).filter((w) => {
      const sr = runs.get(w.key);
      return !!sr && (Math.abs(sr.lo - w.lo) > 0.01 || Math.abs(sr.hi - w.hi) > 0.01);
    });
    if (!moved.length) continue;
    const w = moved.find((x) => x.f <= 1e-6 || x.f >= 1 - 1e-6) ?? moved[0];
    const sr = runs.get(w.key)!;
    const a = sr.lo + h, b = Math.max(a, sr.hi - h);
    const st = w.f <= 1e-6 ? a : w.f >= 1 - 1e-6 ? b : a + w.f * (b - a);
    const place = supportsAlong(sr, st, st, 1e-6)[0];
    const keys = ['edge', 'innerWallId', 'face', 'corner', 'roundBench', 'roundSide', 'seg', 'at', 'flush'] as const;
    const same = keys.every((k) => (k === 'at' ? Math.abs((place.at ?? 0) - s.at) < 1e-6 : place[k] === s[k]));
    if (same) continue;
    for (const k of keys) delete (s as Partial<BenchSupport>)[k];
    Object.assign(s, place);
    changed = true;
  }
  return changed;
}

/** The trestle (of any support, on any tier) under `p` — inside it, or within
 *  `tol` mm of its outline — or null; the nearest wins. */
export function supportMarkAt(marks: TrestleMark[], p: Vec2, tol: number): TrestleMark | null {
  let best: TrestleMark | null = null, bestD = Infinity;
  for (const m of marks) {
    const d = pointInPolygon(p, m.poly) ? 0 : Math.min(...m.poly.map((a, i) => {
      const b = m.poly[(i + 1) % m.poly.length];
      const L2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
      const t = L2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y)) / L2)) : 0;
      return Math.hypot(p.x - (a.x + (b.x - a.x) * t), p.y - (a.y + (b.y - a.y) * t));
    }));
    if (d <= tol && d < bestD) { bestD = d; best = m; }
  }
  return best;
}

/** A temporary figure of a support on its run: from `a` to `b` on the trestle's
 *  middle line, `length` mm apart, ending on another trestle or the bench's end. */
/** A support's figure: from `a` (the station `from` on its run — the nearest
 *  trestle's centre or the bench's end) to its own trestle's centre `b`, which
 *  stands on the `side` of `from` the run counts up (+1) or down (−1). Typed, it
 *  moves the trestle to `from + side · value`. */
export interface SupportSpacing { a: Vec2; b: Vec2; length: number; to: 'support' | 'end'; from: number; side: 1 | -1 }

/**
 * The temporary figures of the trestle `m` — a support slid, selected or about
 * to be placed: on its own run, from its centre to the nearest trestle centre on
 * each side, or to the bench's end where no trestle stands between. A side with
 * nothing to measure (a trestle on the very end) gives none.
 */
export function supportSpacings(marks: TrestleMark[], m: TrestleMark): SupportSpacing[] {
  const others = marks.filter((o) => o.run === m.run && o.support !== m.support).map((o) => o.at);
  const before = others.filter((s) => s < m.at - 1e-6), after = others.filter((s) => s > m.at + 1e-6);
  const out: SupportSpacing[] = [];
  const side = (s: number, to: SupportSpacing['to']): void => {
    const length = Math.abs(m.at - s);
    if (length > 0.5) out.push({ a: m.point(s), b: m.point(m.at), length, to, from: s, side: m.at >= s ? 1 : -1 });
  };
  if (before.length) side(Math.max(...before), 'support'); else side(m.ends[0], 'end');
  if (after.length) side(Math.min(...after), 'support'); else side(m.ends[1], 'end');
  return out;
}

/** The segment whose frame runs through under corner node `k` of a standalone
 *  bench: the one before it (`k - 1`), or the one after (`k`) where the node is
 *  in `benchFrameSwaps`. */
export function nodeFrameWinner(inst: Pick<ProductInstance, 'benchFrameSwaps'>, k: number): number {
  return inst.benchFrameSwaps?.includes(k) ? k : k - 1;
}

/** Swap which segment's frame runs through at corner node `k`. */
export function toggleNodeFrameSwap(inst: Pick<ProductInstance, 'benchFrameSwaps'>, k: number): void {
  const list = (inst.benchFrameSwaps ?? []).filter((x) => x !== k);
  if (!inst.benchFrameSwaps?.includes(k)) list.push(k);
  list.sort((a, b) => a - b);
  if (list.length) inst.benchFrameSwaps = list; else delete inst.benchFrameSwaps;
}

/** `benchFrameSwaps` re-homed after a node was inserted (`delta` 1, after
 *  node `at`) or removed (`delta` -1, node `at`): a removed node's swap goes. */
export function shiftNodeFrameSwaps(inst: Pick<ProductInstance, 'benchFrameSwaps'>, at: number, delta: 1 | -1): void {
  if (!inst.benchFrameSwaps) return;
  const list = inst.benchFrameSwaps.filter((k) => delta > 0 || k !== at).map((k) => (delta > 0 ? (k > at ? k + 1 : k) : (k > at ? k - 1 : k)));
  if (list.length) inst.benchFrameSwaps = list; else delete inst.benchFrameSwaps;
}

/** A standalone slatted sauna bench's corner nodes — interior nodes where its
 *  run turns —, each with the plan region its two segments' ends cover there
 *  (the Bench tool's highlight; world frame). */
export function nodeBenchJoints(inst: ProductInstance): Array<{ node: number; polys: Vec2[][] }> {
  const nodes = inst.nodes;
  if (inst.category !== 'saunabench' || !nodes || nodes.length < 3 || !inst.slats?.length) return [];
  const D = inst.depth > 0 ? inst.depth : 1;
  const out: Array<{ node: number; polys: Vec2[][] }> = [];
  for (let k = 1; k + 1 < nodes.length; k++) {
    const p = nodes[k - 1], c = nodes[k], q = nodes[k + 1];
    const la = dist(p, c), lb = dist(c, q);
    if (la < 1 || lb < 1) continue;
    const cr = (c.x - p.x) * (q.y - c.y) - (c.y - p.y) * (q.x - c.x);
    if (Math.abs(cr) / (la * lb) < 1e-6) continue;
    const pp = k > 1 ? nodes[k - 2] : null, qq = k + 2 < nodes.length ? nodes[k + 2] : null;
    const fp = ribbonJoint(pp, p, c, D), fc = ribbonJoint(p, c, q, D), fq = ribbonJoint(c, q, qq, D);
    const ua = Math.max(0, 1 - D / la), ub = Math.min(1, D / lb);
    out.push({ node: k, polys: [
      [lerp(p, c, ua), c, fc, lerp(fp, fc, ua)],
      [c, lerp(c, q, ub), lerp(fc, fq, ub), fc],
    ] });
  }
  return out;
}

/** One segment of a standalone sauna bench as the plan draws it (world frame):
 *  its ribbon from `a` to `b` (`body`: the back edge on the node line, the front
 *  edge mitred with the neighbours `prev` / `next`) and its slats across it,
 *  back to front. */
export interface SaunaBenchSegment { body: Vec2[]; slats: Vec2[][] }

/** {@link SaunaBenchSegment} of `a`–`b`, the slats at `spans` (`benchSlatSpans`);
 *  null under 1 mm. The Bench tool's preview draws it too. */
export function saunaBenchSegment(a: Vec2, b: Vec2, depth: number, spans: Array<{ off: number; width: number }>, prev: Vec2 | null, next: Vec2 | null): SaunaBenchSegment | null {
  if ((Math.hypot(b.x - a.x, b.y - a.y) || 1) < 1) return null;
  const fa = ribbonJoint(prev, a, b, depth), fb = ribbonJoint(a, b, next, depth);
  const D = depth > 0 ? depth : 1;
  const slats = spans.map((s) => {
    const t0 = s.off / D, t1 = (s.off + s.width) / D;
    return [lerp(a, fa, t0), lerp(b, fb, t0), lerp(b, fb, t1), lerp(a, fa, t1)];
  });
  return { body: [a, b, fb, fa], slats };
}

/** A standalone sauna bench as the plan draws it and the DXF writes it (world
 *  frame): its segments in run order, its trestles (`benchSupportMarks`) and
 *  its closed ends' lines (`closedEndLines`). One without nodes is the straight
 *  run along its footprint's far edge, with neither. */
export interface SaunaBenchPlan { segments: SaunaBenchSegment[]; trestles: TrestleMark[]; ends: Vec2[][] }

export function saunaBenchPlan(inst: ProductInstance): SaunaBenchPlan {
  const nodes = inst.nodes && inst.nodes.length >= 2 ? inst.nodes
    : [instanceToWorld(inst, { x: -inst.width / 2, y: inst.depth / 2 }), instanceToWorld(inst, { x: inst.width / 2, y: inst.depth / 2 })];
  const spans = inst.slats?.length ? benchSlatSpans({ slats: inst.slats, gaps: inst.gaps ?? [] }) : [];
  const segments: SaunaBenchSegment[] = [];
  for (let i = 0; i + 1 < nodes.length; i++) {
    const s = saunaBenchSegment(nodes[i], nodes[i + 1], inst.depth, spans, i > 0 ? nodes[i - 1] : null, i + 2 < nodes.length ? nodes[i + 2] : null);
    if (s) segments.push(s);
  }
  return { segments, trestles: benchSupportMarks(inst), ends: closedEndLines(inst) };
}

/** Walk a standalone sauna bench (a node object, world frame) into `sink`: per
 *  segment of its run, the plan's mitred ribbon (back edge on the node line,
 *  front edge mitred with the neighbours) cut into its slats, and its run. At a
 *  corner node the seat stays mitred while the frames meet as a room's mitred
 *  corner does: the winner's (`nodeFrameWinner`) runs through to the other's
 *  far face, its last stud on the mitre, the other's stops at the winner's
 *  frame — notched where it runs on under the winner's front board. */
function eachNodeBench(inst: ProductInstance, sink: BenchSink, floor = 0): void {
  const nodes = inst.nodes;
  if (inst.category !== 'saunabench' || !nodes || nodes.length < 2 || !inst.slats?.length) return;
  const spans = benchSlatSpans({ slats: inst.slats, gaps: inst.gaps });
  const top = objectHeight(inst);
  const D = inst.depth > 0 ? inst.depth : 1;
  const skirt = skirtingOf(inst);
  const front = frontBoard(inst);
  const F = front ? Math.min(D, front.d) : D;
  const tag: BenchTag = { id: inst.id, kind: 'band', level: 'high' };
  const thick = spans.map((_, k) => top - slatBase(inst, k, top));
  // Each segment is its own run: a support stands on one (`seg`), its station
  // along that segment from its first node.
  const lens = nodes.slice(1).map((b, i) => Math.hypot(b.x - nodes[i].x, b.y - nodes[i].y));
  for (let i = 0; i + 1 < nodes.length; i++) {
    const a = nodes[i], b = nodes[i + 1], Ls = lens[i];
    if (Ls < 1) continue;
    const prev = i > 0 ? nodes[i - 1] : null, next = i + 2 < nodes.length ? nodes[i + 2] : null;
    const fa = ribbonJoint(prev, a, b, inst.depth), fb = ribbonJoint(a, b, next, inst.depth);
    const at0 = (u: number, d: number): Vec2 => lerp(lerp(a, b, u), lerp(fa, fb, u), d / D);
    const quads = spans.map((s) => [at0(0, s.off), at0(1, s.off), at0(1, s.off + s.width), at0(0, s.off + s.width)]);
    // Its ends are its first node's and its last's, always closable: it stands on
    // its own, the floor under it.
    const flags = closedFlags(inst);
    const [c0, c1] = quads.length && (sink.end || flags[0] || flags[1])
      ? runEnds(inst.id, quads, thick, [flags[0] && i === 0, flags[1] && i === nodes.length - 2], () => true, (e) => { if (e.side === 'start' ? i === 0 : i === nodes.length - 2) sink.end?.(e); })
      : [false, false];
    const close: Closure | null = c0 || c1 ? { frame: seatSlab(inst), start: c0, end: c1, floor: () => floor + closureClearance(inst) } : null;
    quads.forEach((poly, k) => {
      for (const p of slatPieces(poly, slatBase(inst, k, top), top, null, close)) sink.piece({ ...tag, ...p });
    });
    const dir = { x: (b.x - a.x) / Ls, y: (b.y - a.y) / Ls }, n = { x: dir.y, y: -dir.x };
    const sOf = (p: Vec2): number => (p.x - a.x) * dir.x + (p.y - a.y) * dir.y;
    // Its frame's end at a corner node: the winner's on the other segment's
    // line (its back or its frame's front) that leaves it the longer, the
    // loser's on the winner's that leaves it the shorter.
    const lineAt = (seg: number, d: number): { p: Vec2; u: Vec2 } => {
      const p0 = nodes[seg], p1 = nodes[seg + 1], l = lens[seg], u = { x: (p1.x - p0.x) / l, y: (p1.y - p0.y) / l };
      return { p: { x: p0.x + u.y * d, y: p0.y - u.x * d }, u };
    };
    const meet = (A: { p: Vec2; u: Vec2 }, B: { p: Vec2; u: Vec2 }): Vec2 => {
      const den = A.u.x * B.u.y - A.u.y * B.u.x;
      const t = ((B.p.x - A.p.x) * B.u.y - (B.p.y - A.p.y) * B.u.x) / den;
      return { x: A.p.x + A.u.x * t, y: A.p.y + A.u.y * t };
    };
    // `back` / `front`: the depths of the band whose ends these are (the frame's,
    // or the skirting's), on both segments.
    const bandEnd = (end: 0 | 1, back: number, front: number): { p0: Vec2; pD: Vec2; wins: boolean } | null => {
      const k = i + end, j = end ? i + 1 : i - 1;
      if (j < 0 || j > nodes.length - 2 || lens[j] < 1) return null;
      const uj = lineAt(j, 0).u;
      if (Math.abs(dir.x * uj.y - dir.y * uj.x) < 1e-6) return null;
      const wins = nodeFrameWinner(inst, k) === i;
      const pick = (off: number): { p0: Vec2; pD: Vec2; s: number } => {
        const o = lineAt(j, off);
        return { p0: meet(lineAt(i, 0), o), pD: meet(lineAt(i, D), o), s: sOf(meet(lineAt(i, (back + front) / 2), o)) };
      };
      const [cb, cf] = [pick(back), pick(front)];
      const longer = end ? cf.s > cb.s : cf.s < cb.s;
      const c = wins === longer ? cf : cb;
      return { p0: c.p0, pD: c.pD, wins };
    };
    const runAt = (e0: ReturnType<typeof bandEnd>, e1: ReturnType<typeof bandEnd>): ((u: number, d: number) => Vec2) => {
      if (!e0 && !e1) return at0;
      const lo = e0?.p0 ?? a, li = e0?.pD ?? fa, ro = e1?.p0 ?? b, ri = e1?.pD ?? fb;
      return (u, d) => lerp(lerp(lo, ro, u), lerp(li, ri, u), d / D);
    };
    const e0 = bandEnd(0, 0, F), e1 = bandEnd(1, 0, F);
    const atF = runAt(e0, e1);
    // Its skirting's ends: as its frame's, on the two skirtings' faces.
    const sk = skirt ? (() => { const t = Math.max(...skirt.thickness), back = runSkirtBack(skirt, 0, undefined, F); return [F - back - t, F - back] as const; })() : null;
    const skirtAt = sk && (e0 || e1) ? closedRun(runAt(bandEnd(0, sk[0], sk[1]), bandEnd(1, sk[0], sk[1])), D, close).at : null;
    const mitres = ([[e0, 0, a, fa], [e1, 1, b, fb]] as const).filter(([e]) => e?.wins).map(([, end, p, q]) => ({ end, at: (d: number): Vec2 => lerp(p, q, d / D) }));
    const { at, t0, t1 } = closedRun(atF, D, close);
    const across = (s: number, d: number): Vec2 => ({ x: a.x + dir.x * s + n.x * d, y: a.y + dir.y * s + n.y * d });
    // Its trestles stand under its seat, but where its frame runs through.
    const sMin = Math.max(sOf(at(0, 0)), sOf(at(0, F)), ...(e0?.wins ? [] : [sOf(at0(0, 0)), sOf(at0(0, F))]));
    const sMax = Math.min(sOf(at(1, 0)), sOf(at(1, F)), ...(e1?.wins ? [] : [sOf(at0(1, 0)), sOf(at0(1, F))]));
    sink.run(tag, {
      strip: (d0, d1) => [at(0, d0), at(1, d0), at(1, d1), at(0, d1)],
      at,
      across,
      trestles: segSupportsOn(inst, i, lens).map(({ s, at: g }) => ({ id: s.id, at: trestleCentre(g, s.flush, sMin, sMax) })),
      ends: [sMin, sMax],
      ...(mitres.length ? { mitres } : {}),
      ...(skirtAt ? { skirtingStrip: (d0: number, d1: number) => [skirtAt(0, d0), skirtAt(1, d0), skirtAt(1, d1), skirtAt(0, d1)] } : {}),
      support: { kind: 'run', host: { seg: i }, len: Ls, start: 0, end: Ls, tier: 0, face: [a, b, fb, fa] },
      len: e0 || e1 ? dist(at(0, F / 2), at(1, F / 2)) : Ls - t0 - t1,
      depth: F,
      top,
      slab: seatSlab(inst),
      frontBase: front ? top - front.hang : undefined,
      skirting: skirt,
      skirtingFloor: floor,
    });
  }
}

/** A standalone sauna bench's skirting's front face: its depth from the node
 *  line (mm), as its structure lays it; null when it has no skirting. */
export function nodeBenchSkirtFront(inst: ProductInstance): number | null {
  const skirt = skirtingOf(inst);
  if (inst.category !== 'saunabench' || !skirt || !inst.slats?.length) return null;
  const D = inst.depth > 0 ? inst.depth : 1, front = frontBoard(inst);
  const F = front ? Math.min(D, front.d) : D;
  return F - runSkirtBack(skirt, 0, undefined, F);
}

/**
 * The pieces of a standalone sauna bench (a node object), world frame: per
 * segment of its run, its slats, then its frame, trestles and skirting — its
 * skirting and closed ends on `floor` (the top of a grille under it). Empty for
 * anything that is not a slatted sauna bench.
 */
export function nodeBenchSolids(inst: ProductInstance, floor = 0): Solid[] {
  // Each segment its own id while the corner passes run (a loser's frame is
  // notched under the winner's front board, never its own), dropped after.
  const out: BenchSolid[] = [];
  let seg = 0;
  const tag = (): BenchTag => ({ id: `${seg}`, kind: 'band', level: 'high' });
  eachNodeBench(inst, {
    piece: ({ part, poly, base, top, sweep }) => out.push({ ...tag(), part, poly, base, top, ...(sweep ? { sweep } : {}) }),
    run: (_tag, run) => { for (const s of benchStructure(run)) out.push({ ...tag(), ...s }); seg++; },
  }, floor);
  return notchSeats(notchUnderBoards(out)).map(({ part, poly, base, top, sweep, support }) => ({ part, poly, base, top, ...(sweep ? { sweep } : {}), ...(support != null ? { support } : {}) }));
}

/** Every end of a room's slatted benches (its wall and partition benches;
 *  room frame) or of a standalone sauna bench (world) that can close, open or
 *  closed — any but one another bench carries on from: where the Bench end tool
 *  can close one, and the closed ones' plan marks. */
export function benchEndMarks(inst: ProductInstance, heights: Pick<ProductTemplate, 'benchTierHeights'> = { benchTierHeights: inst.benchTierHeights }): BenchEndMark[] {
  const out: BenchEndMark[] = [];
  const sink: BenchSink = { piece: () => {}, run: () => {}, end: (e) => out.push(e) };
  if (inst.category === 'saunabench') eachNodeBench(inst, sink);
  else if (isPolyRoom(inst)) eachRoomBench(inst, heights, sink);
  return out;
}

/** Whether any of an object's benches has an end closed (stored: closed only
 *  where it can) — the plan looks for its marks only then. */
export function hasClosedEnds(inst: ProductInstance): boolean {
  const any = (b: { closeStart?: boolean; closeEnd?: boolean }): boolean => !!(b.closeStart || b.closeEnd);
  return any(inst) || !!inst.polyBenches?.some(any) || !!inst.innerWalls?.some((iw) => [...(iw.benches?.a ?? []), ...(iw.benches?.b ?? [])].some(any));
}

/** The frame and trestles' colour: the bench's own a shade darker, so the
 *  structure reads apart from the slats. */
export function structureColor(hex: string): string {
  const m = /^#([0-9a-f]{6})/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const ch = (v: number): string => Math.round(v * 0.72).toString(16).padStart(2, '0');
  return `#${ch((n >> 16) & 255)}${ch((n >> 8) & 255)}${ch(n & 255)}`;
}
